import { A as formatRange, C as WebCodecsFrameDecoder, D as DEFAULT_HALF_MS, E as defaultImageDecoder, O as KEY_STEP_MS, S as VideoTrack, T as MARK, _ as rewriteCssUrls, a as COMMON_STYLE_KEYS, b as CanvasTracks, c as attributesOf, d as cssEscape, f as describeElement, g as Mirror, h as xpathFor, i as Timeline, j as CursorOverlay, k as SelectionModel, l as boxModelOf, m as selectorFor, n as describeGap, o as accessibleName, p as isHashedToken, r as formatTime, s as ancestorsOf, t as Player, u as computedStylesOf, v as rewriteSrcset, w as CommandReplayer, x as reasonLabel, y as HighlightOverlay } from "./chunks/player-BVT01yhQ.js";
//#region src/panels/types.ts
var rel = (input, abs) => Math.max(0, abs - input.startTs);
function fmtClock(ms) {
	const total = Math.max(0, Math.floor(ms / 1e3));
	const h = Math.floor(total / 3600);
	const m = Math.floor(total % 3600 / 60);
	const s = total % 60;
	const frac = Math.floor(Math.max(0, ms) % 1e3 / 100);
	return `${h ? `${h}:` : ""}${h ? String(m).padStart(2, "0") : m}:${String(s).padStart(2, "0")}.${frac}`;
}
function fmtMs(ms) {
	if (ms == null || !Number.isFinite(ms)) return "–";
	if (ms < 1e3) return `${Math.round(ms)} ms`;
	return `${(ms / 1e3).toFixed(2)} s`;
}
function fmtBytes(b) {
	if (b == null || !Number.isFinite(b)) return "–";
	if (b < 1024) return `${b} B`;
	if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
	return `${(b / 1024 / 1024).toFixed(2)} MB`;
}
/** Try to pretty-print a serialized argument; returns null when it is not JSON. */
function tryJson(s) {
	const t = s.trim();
	if (!(t.startsWith("{") || t.startsWith("["))) return null;
	try {
		return JSON.parse(t);
	} catch {
		return null;
	}
}
/** Minimal DOM builder used by the vanilla renderers. */
function h(tag, attrs = {}, ...children) {
	const el = document.createElement(tag);
	for (const [k, v] of Object.entries(attrs)) {
		if (v === void 0 || v === false) continue;
		if (typeof v === "function") el.addEventListener(k.replace(/^on/, "").toLowerCase(), v);
		else if (k === "class") el.className = String(v);
		else if (k === "text") el.textContent = String(v);
		else el.setAttribute(k, String(v));
	}
	for (const c of children) {
		if (c === null || c === void 0 || c === false) continue;
		el.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
	}
	return el;
}
function clear(el) {
	while (el.firstChild) el.removeChild(el.firstChild);
}
//#endregion
//#region src/panels/console.ts
var FRAME_RE = /^\s*at\s+(?:(.*?)\s+\()?((?:https?|file|webpack|blob):\/\/[^\s)]+?|[^\s():]+?):(\d+):(\d+)\)?\s*$/;
var FRAME_RE_FF = /^\s*(?:([^@\s]*)@)?((?:https?|file|webpack|blob):\/\/[^\s)]+?|[^\s():]+?):(\d+):(\d+)\s*$/;
/** Parse a V8- or SpiderMonkey-style stack into frames. */
function parseStack(stack) {
	if (!stack) return [];
	const out = [];
	for (const line of stack.split("\n")) {
		const m = FRAME_RE.exec(line) ?? FRAME_RE_FF.exec(line);
		if (!m) continue;
		out.push({
			raw: line.trim(),
			function: m[1]?.trim() || void 0,
			file: m[2],
			line: Number(m[3]),
			column: Number(m[4])
		});
	}
	return out;
}
function deriveConsole(input, filter = {}) {
	const entries = [];
	for (const te of input.events) {
		const e = te.event;
		if (e.type === "console") entries.push({
			key: `${te.seq}:${te.idx}`,
			t: te.t,
			level: e.level,
			args: e.args,
			parsed: e.args.map(tryJson),
			stack: e.stack,
			frames: parseStack(e.stack),
			future: false,
			current: false
		});
		else if (e.type === "error") entries.push({
			key: `${te.seq}:${te.idx}`,
			t: te.t,
			level: "exception",
			args: [e.message],
			parsed: [null],
			stack: e.stack,
			frames: parseStack(e.stack),
			kind: e.kind,
			fingerprint: e.fingerprint,
			source: e.source || e.lineno ? {
				file: e.source,
				line: e.lineno,
				column: e.colno
			} : void 0,
			future: false,
			current: false
		});
	}
	const text = filter.text?.toLowerCase();
	const filtered = entries.filter((en) => (!filter.levels || filter.levels.has(en.level)) && (!text || en.args.join(" ").toLowerCase().includes(text) || (en.stack ?? "").toLowerCase().includes(text)));
	let lastIdx = -1;
	filtered.forEach((en, i) => {
		en.future = en.t > input.now;
		if (!en.future) lastIdx = i;
	});
	if (lastIdx >= 0) filtered[lastIdx].current = true;
	return filtered;
}
function consoleCounts(entries) {
	const c = {
		log: 0,
		info: 0,
		warn: 0,
		error: 0,
		debug: 0,
		trace: 0,
		exception: 0
	};
	for (const e of entries) c[e.level]++;
	return c;
}
/**
* Symbolication with an in-memory cache keyed by (release, file, line, column).
* Missing backend (404/501) is remembered so we stop asking.
*/
var Symbolicator = class {
	fetcher;
	release;
	cache = /* @__PURE__ */ new Map();
	unavailable = false;
	constructor(fetcher, release) {
		this.fetcher = fetcher;
		this.release = release;
	}
	get isUnavailable() {
		return this.unavailable;
	}
	key(f) {
		return `${this.release ?? ""}|${f.file ?? ""}|${f.line ?? ""}|${f.column ?? ""}`;
	}
	cached(frames) {
		return frames.map((f) => this.cache.get(this.key(f)) ?? null);
	}
	async symbolicate(frames) {
		if (!this.fetcher || this.unavailable) return this.cached(frames);
		const missing = frames.filter((f) => f.file && f.line != null && !this.cache.has(this.key(f)));
		if (missing.length) try {
			(await this.fetcher(this.release, missing.map(({ file, line, column, function: fn }) => ({
				file,
				line,
				column,
				function: fn
			})))).forEach((sf, i) => {
				const src = missing[i];
				if (src) this.cache.set(this.key(src), sf);
			});
		} catch (err) {
			const status = err.status;
			if (status === 404 || status === 501) this.unavailable = true;
		}
		return this.cached(frames);
	}
};
//#endregion
//#region src/panels/network.ts
var SLOW_MS = 1e3;
function deriveNetwork(input, filter = {}) {
	const byReq = /* @__PURE__ */ new Map();
	const order = [];
	for (const te of input.events) {
		const e = te.event;
		if (e.type !== "network") continue;
		const { host, path } = splitUrl(e.url);
		const existing = byReq.get(e.reqId);
		if (existing) {
			if (e.kind === "ws" && e.wsPhase === "message" && e.wsDirection && e.wsDataType) {
				existing.wsFrames.push({
					t: te.t,
					direction: e.wsDirection,
					dataType: e.wsDataType,
					bytes: e.wsBytes,
					text: e.wsText
				});
				existing.wsBytes += e.wsBytes ?? 0;
			}
			applyWsPhase(existing, e);
			Object.assign(existing, {
				status: e.status ?? existing.status,
				durationMs: e.durationMs ?? existing.durationMs,
				size: e.size ?? existing.size,
				failed: existing.failed || !!e.failed || e.kind !== "ws" && (e.status ?? 0) >= 400,
				timing: e.timing ?? existing.timing,
				resHeaders: e.resHeaders ?? existing.resHeaders,
				resBody: e.resBody ?? existing.resBody,
				reqBodyCut: e.reqBodyCut || existing.reqBodyCut,
				reqBodyBytes: e.reqBodyBytes ?? existing.reqBodyBytes,
				resBodySkipped: e.resBodySkipped || existing.resBodySkipped,
				traceId: e.traceId ?? existing.traceId,
				wsSent: Math.max(existing.wsSent, e.wsSent ?? 0),
				wsReceived: Math.max(existing.wsReceived, e.wsReceived ?? 0),
				events: existing.events + 1
			});
			existing.end = Math.max(existing.end, existing.t + (existing.durationMs ?? 0), te.t);
			existing.slow = existing.kind === "ws" ? false : (existing.durationMs ?? 0) >= SLOW_MS;
			continue;
		}
		const row = {
			key: `${te.seq}:${te.idx}`,
			t: te.t,
			end: te.t + (e.durationMs ?? 0),
			kind: e.kind,
			reqId: e.reqId,
			method: (e.method ?? (e.kind === "resource" ? "GET" : "GET")).toUpperCase(),
			url: e.url,
			path,
			host,
			status: e.status,
			durationMs: e.durationMs,
			size: e.size,
			initiatorType: e.initiatorType,
			traceId: e.traceId,
			operation: e.operation,
			failed: !!e.failed || e.kind !== "ws" && (e.status ?? 0) >= 400,
			slow: e.kind === "ws" ? false : (e.durationMs ?? 0) >= SLOW_MS,
			timing: e.timing,
			reqHeaders: e.reqHeaders,
			resHeaders: e.resHeaders,
			reqBody: e.reqBody,
			resBody: e.resBody,
			reqBodyCut: e.reqBodyCut,
			reqBodyBytes: e.reqBodyBytes,
			resBodySkipped: e.resBodySkipped,
			future: false,
			current: false,
			events: 1,
			wsSent: e.wsSent ?? 0,
			wsReceived: e.wsReceived ?? 0,
			wsBytes: e.wsBytes ?? 0,
			wsFrames: e.kind === "ws" && e.wsPhase === "message" && e.wsDirection && e.wsDataType ? [{
				t: te.t,
				direction: e.wsDirection,
				dataType: e.wsDataType,
				bytes: e.wsBytes,
				text: e.wsText
			}] : [],
			wsHandshakeMs: e.kind === "ws" && e.wsPhase === "open" ? e.durationMs : void 0,
			wsLifetimeMs: e.kind === "ws" && e.wsPhase === "close" ? e.durationMs : void 0,
			wsCloseCode: e.kind === "ws" && e.wsPhase === "close" ? e.status : void 0,
			wsState: initialWsState(e)
		};
		byReq.set(e.reqId, row);
		order.push(row);
	}
	const text = filter.text?.toLowerCase();
	const rows = order.filter((r) => (!filter.kinds || filter.kinds.has(r.kind)) && (!filter.failedOnly || r.failed) && (!filter.slowOnly || r.slow) && (!text || r.url.toLowerCase().includes(text) || (r.operation ?? "").toLowerCase().includes(text) || String(r.status ?? "").includes(text)));
	let last = -1;
	rows.forEach((r, i) => {
		r.future = r.t > input.now;
		if (!r.future) last = i;
	});
	if (last >= 0) rows[last].current = true;
	for (const r of rows) {
		if (r.kind !== "ws") continue;
		if (r.wsState === "open" || r.wsState === "connecting") r.wsLifetimeMs = Math.max(0, input.now - r.t);
	}
	return rows;
}
function initialWsState(e) {
	if (e.kind !== "ws") return "closed";
	if (e.wsPhase === "error" || e.failed) return e.wsPhase === "close" || e.status === 1006 ? "dropped" : "error";
	if (e.wsPhase === "close") return e.status === 1006 ? "dropped" : "closed";
	if (e.wsPhase === "open") return "open";
	return "connecting";
}
function applyWsPhase(row, e) {
	if (e.kind !== "ws") return;
	if (e.wsPhase === "open" && e.durationMs != null) row.wsHandshakeMs = e.durationMs;
	if (e.wsPhase === "close") {
		row.wsCloseCode = e.status;
		row.wsLifetimeMs = e.durationMs;
		row.wsState = e.failed || e.status === 1006 ? "dropped" : "closed";
	}
	if (e.wsPhase === "error" && row.wsState !== "closed" && row.wsState !== "dropped") row.wsState = "error";
}
var WS_CLOSE = {
	1e3: "Normal",
	1001: "Going away",
	1002: "Protocol error",
	1003: "Unsupported data",
	1005: "No status",
	1006: "Dropped",
	1007: "Bad payload",
	1008: "Policy violation",
	1009: "Too large",
	1011: "Server error",
	1012: "Restart",
	1013: "Try later",
	1014: "Bad gateway",
	1015: "TLS failed"
};
function wsCloseLabel(code) {
	if (code == null) return void 0;
	return WS_CLOSE[code] ?? `Code ${code}`;
}
function wsStateLabel(row) {
	if (row.wsState === "open") return "Open";
	if (row.wsState === "connecting") return "Connecting";
	if (row.wsState === "error") return "Error";
	if (row.wsState === "dropped") return wsCloseLabel(row.wsCloseCode) ?? "Dropped";
	return wsCloseLabel(row.wsCloseCode) ?? "Closed";
}
/** Send/receive ticks as a percentage of the connection's lifetime, for the conversation tape. */
function wsSparkTicks(row) {
	const start = row.t;
	const end = Math.max(row.end, start + 1);
	return row.wsFrames.map((f) => ({
		pct: Math.min(100, Math.max(0, (f.t - start) / (end - start) * 100)),
		direction: f.direction
	}));
}
function wsCadenceMs(row) {
	if (row.wsFrames.length < 2) return void 0;
	let sum = 0;
	for (let i = 1; i < row.wsFrames.length; i++) sum += row.wsFrames[i].t - row.wsFrames[i - 1].t;
	return Math.round(sum / (row.wsFrames.length - 1));
}
function splitUrl(url) {
	try {
		const u = new URL(url, "http://relative.invalid");
		return {
			host: u.host === "relative.invalid" ? "" : u.host,
			path: u.pathname + (u.search ? u.search : "")
		};
	} catch {
		return {
			host: "",
			path: url
		};
	}
}
function summarizeNetwork(rows) {
	const s = {
		requests: rows.length,
		failed: 0,
		slow: 0,
		bytes: 0,
		byKind: {}
	};
	for (const r of rows) {
		if (r.failed) s.failed++;
		if (r.slow) s.slow++;
		s.bytes += r.kind === "ws" ? r.wsBytes : r.size ?? 0;
		s.byKind[r.kind] = (s.byKind[r.kind] ?? 0) + 1;
	}
	return s;
}
var REDACT = /* @__PURE__ */ new Set([
	"authorization",
	"cookie",
	"set-cookie",
	"x-api-key",
	"x-auth-token",
	"proxy-authorization"
]);
/** Build a cURL command with sensitive headers redacted. */
function toCurl(row) {
	const parts = [`curl -X ${row.method}`, `'${row.url.replace(/'/g, "%27")}'`];
	for (const [k, v] of Object.entries(row.reqHeaders ?? {})) {
		const val = REDACT.has(k.toLowerCase()) ? "<redacted>" : v.replace(/'/g, "%27");
		parts.push(`-H '${k}: ${val}'`);
	}
	if (row.reqBody) parts.push(`--data-raw '${row.reqBody.replace(/'/g, "'\\''")}'`);
	return parts.join(" \\\n  ");
}
/** Timing segments for a row (ms), in waterfall order. */
function timingSegments(row) {
	const t = row.timing;
	if (!t) return row.durationMs != null ? [{
		name: "total",
		ms: row.durationMs
	}] : [];
	const segs = [];
	if (t.dns) segs.push({
		name: "dns",
		ms: t.dns
	});
	if (t.connect) segs.push({
		name: "connect",
		ms: t.connect
	});
	if (t.tls) segs.push({
		name: "tls",
		ms: t.tls
	});
	if (t.ttfb) segs.push({
		name: "waiting",
		ms: t.ttfb
	});
	if (t.download) segs.push({
		name: "download",
		ms: t.download
	});
	return segs;
}
/** Find the trace linked to a request (by requestId first, then traceId). */
function traceForRow(row, traces) {
	return traces.find((t) => t.requestId === row.reqId) ?? (row.traceId ? traces.find((t) => t.traceId === row.traceId) : void 0);
}
/** Nest spans into a tree for rendering. */
function spanTree(trace) {
	const byParent = /* @__PURE__ */ new Map();
	for (const s of trace.spans) {
		const list = byParent.get(s.parentSpanId) ?? [];
		list.push(s);
		byParent.set(s.parentSpanId, list);
	}
	const ids = new Set(trace.spans.map((s) => s.spanId));
	const roots = trace.spans.filter((s) => !s.parentSpanId || !ids.has(s.parentSpanId)).sort((a, b) => a.startMs - b.startMs);
	const out = [];
	const walk = (s, depth) => {
		out.push({
			span: s,
			depth
		});
		for (const c of (byParent.get(s.spanId) ?? []).sort((a, b) => a.startMs - b.startMs)) walk(c, depth + 1);
	};
	for (const r of roots) walk(r, 0);
	return out;
}
//#endregion
//#region ../protocol/dist/webrtc.js
/**
* Composite quality score, computed identically in the SDK (for triggers) and
* the worker (for the Calls page) so the two never disagree. 100 is perfect.
*/
function callQualityScore(m) {
	const dur = Math.max(m.durationMs ?? 1, 1);
	const penalties = [
		clamp01(((m.rttMs ?? 0) - 150) / 450) * 25,
		clamp01((m.lossPct ?? 0) / 10) * 30,
		clamp01(((m.jitterMs ?? 0) - 20) / 100) * 15,
		clamp01((m.freezeMs ?? 0) / dur / .05) * 20,
		clamp01((m.concealmentPct ?? 0) / 10) * 10
	];
	return Math.round(Math.max(0, 100 - penalties.reduce((a, b) => a + b, 0)));
}
function clamp01(n) {
	return n < 0 ? 0 : n > 1 ? 1 : n;
}
//#endregion
//#region src/panels/media.ts
var LANE_SPEC = {
	rtt: {
		label: "Round trip time",
		unit: "ms",
		colorVar: "--viz-s1",
		floor: 100,
		higherIsWorse: true
	},
	loss: {
		label: "Packet loss",
		unit: "%",
		colorVar: "--viz-critical",
		floor: 5,
		higherIsWorse: true
	},
	jitter: {
		label: "Jitter",
		unit: "ms",
		colorVar: "--viz-s4",
		floor: 30,
		higherIsWorse: true
	},
	fps: {
		label: "Frame rate",
		unit: "fps",
		colorVar: "--viz-s3",
		floor: 30,
		higherIsWorse: false
	},
	kbps: {
		label: "Bitrate",
		unit: "kbps",
		colorVar: "--viz-s7",
		floor: 200,
		higherIsWorse: false
	},
	audio: {
		label: "Audio level",
		unit: "",
		colorVar: "--viz-s2",
		floor: 1,
		higherIsWorse: false
	}
};
var LANE_ORDER = [
	"rtt",
	"loss",
	"jitter",
	"fps",
	"kbps",
	"audio"
];
/** Human label for a lifecycle event, plus how loudly to render it. */
function describeMarker(e) {
	switch (e.kind) {
		case "pc_created": return {
			label: `Call created (${e.iceServerCount} ICE server${e.iceServerCount === 1 ? "" : "s"}${e.hasTurn ? ", TURN available" : ""})`,
			severity: "info"
		};
		case "connection_state": return {
			label: `Connection ${e.state}`,
			severity: e.state === "failed" ? "error" : e.state === "disconnected" ? "warn" : "info"
		};
		case "ice_state": return {
			label: `ICE ${e.state}`,
			severity: e.state === "failed" ? "error" : e.state === "disconnected" ? "warn" : "info"
		};
		case "ice_gathering": return {
			label: `ICE gathering ${e.state}`,
			severity: "info"
		};
		case "signaling_state": return {
			label: `Signaling ${e.state}`,
			severity: "info"
		};
		case "ice_candidate_error": return {
			label: `ICE candidate error ${e.errorCode}${e.url ? ` from ${e.url}` : ""}`,
			severity: "warn"
		};
		case "candidate_pair_change": return {
			label: `Media path ${e.localType} → ${e.remoteType}${e.relayed ? " (relayed through TURN)" : " (direct)"}`,
			severity: e.relayed ? "warn" : "info"
		};
		case "track_added": return {
			label: `${e.direction} ${e.media} track added${e.label ? ` · ${e.label}` : ""}`,
			severity: "info"
		};
		case "track_enabled": return {
			label: `${e.direction} ${e.media} ${e.enabled ? "enabled" : "muted by the application"}`,
			severity: e.enabled ? "info" : "warn"
		};
		case "track_device_change": return {
			label: `${e.direction} ${e.media} device changed${e.label ? ` → ${e.label}` : ""}`,
			severity: "info"
		};
		case "track_muted": return {
			label: `${e.direction} ${e.media} stopped arriving`,
			severity: "error"
		};
		case "track_unmuted": return {
			label: `${e.direction} ${e.media} resumed${e.mutedMs ? ` after ${fmtDur(e.mutedMs)}` : ""}`,
			severity: "info"
		};
		case "track_ended": return {
			label: `${e.direction} ${e.media} track ended`,
			severity: "info"
		};
		case "codec_change": return {
			label: `${e.direction} ${e.media} codec → ${e.mimeType}`,
			severity: "info"
		};
		case "resolution_change": return {
			label: `${e.direction} resolution → ${e.w}×${e.h}${e.fps ? ` at ${Math.round(e.fps)} fps` : ""}`,
			severity: e.h <= 240 ? "warn" : "info"
		};
		case "ice_restart": return {
			label: "ICE restart (recovering from a network change)",
			severity: "warn"
		};
		case "pc_closed": return {
			label: `Call closed after ${fmtDur(e.durationMs)}`,
			severity: "info"
		};
		case "media_acquired": return {
			label: `${e.source === "display" ? "Screen" : "Camera and microphone"} acquired${e.w && e.h ? ` at ${e.w}×${e.h}` : ""}${e.arrivedMuted ? " (arrived muted)" : ""}`,
			severity: e.arrivedMuted ? "warn" : "info"
		};
		case "media_error": return {
			label: `${e.source === "display" ? "Screen capture" : "Camera and microphone"} failed: ${mediaErrorLabel(e.error)}`,
			severity: "error"
		};
		case "device_change": return {
			label: `Devices changed (${e.audioIn} mic, ${e.audioOut} speaker, ${e.videoIn} camera)${deviceNames(e.devices)}`,
			severity: "info"
		};
		case "output_device_change": return {
			label: `Speaker output changed${e.isDefault ? " → system default" : ""}`,
			severity: "info"
		};
		case "device_inventory": return {
			label: `${e.audioIn} microphone${e.audioIn === 1 ? "" : "s"}, ${e.audioOut} speaker${e.audioOut === 1 ? "" : "s"}, ${e.videoIn} camera${e.videoIn === 1 ? "" : "s"} available${deviceNames(e.devices)}`,
			severity: e.audioIn === 0 && e.videoIn === 0 ? "warn" : "info"
		};
		case "data_channel": return {
			label: `Data channel "${e.label}" ${e.state}`,
			severity: "info"
		};
		case "sdp": return {
			label: `${e.direction} ${e.sdpType} (redacted)`,
			severity: "info"
		};
		default: return {
			label: e.kind,
			severity: "info"
		};
	}
}
function deviceNames(devices) {
	const labels = devices?.filter((d) => d.label).map((d) => `${d.label}${d.isDefault ? " (default)" : ""}`) ?? [];
	return labels.length ? ` · ${labels.join(", ")}` : "";
}
function mediaErrorLabel(k) {
	switch (k) {
		case "permission_denied": return "permission denied";
		case "device_not_found": return "no device found";
		case "device_in_use": return "device already in use by another application";
		case "constraints_unsatisfied": return "requested constraints unsatisfiable";
		case "aborted": return "aborted";
		case "security": return "blocked by security policy";
		case "type_error": return "invalid constraints";
		default: return k.replace(/_/g, " ");
	}
}
function fmtDur(ms) {
	if (ms < 1e3) return `${Math.round(ms)} ms`;
	if (ms < 6e4) return `${(ms / 1e3).toFixed(1)} s`;
	return `${Math.floor(ms / 6e4)}m ${Math.round(ms % 6e4 / 1e3)}s`;
}
/** Short "1.2 s" / "820 ms" for setup times and durations. */
var formatCallDuration = fmtDur;
/** Pick the metric a lane reads out of a sample, aggregating across tracks. */
function laneValue(id, s) {
	const tracks = s.tracks ?? [];
	switch (id) {
		case "rtt": return s.rttMs;
		case "loss": {
			const vals = tracks.map((t) => t.lossPct).filter((v) => typeof v === "number");
			return vals.length ? Math.max(...vals) : void 0;
		}
		case "jitter": {
			const vals = tracks.map((t) => t.jitterMs).filter((v) => typeof v === "number");
			return vals.length ? Math.max(...vals) : void 0;
		}
		case "fps": {
			const vals = tracks.filter((t) => t.media === "video").map((t) => t.fps).filter((v) => typeof v === "number");
			return vals.length ? Math.min(...vals) : void 0;
		}
		case "kbps": {
			const vals = tracks.map((t) => t.kbps).filter((v) => typeof v === "number");
			return vals.length ? vals.reduce((a, b) => a + b, 0) : void 0;
		}
		case "audio": {
			const vals = tracks.filter((t) => t.media === "audio").map((t) => t.audioLevel).filter((v) => typeof v === "number");
			return vals.length ? Math.max(...vals) : void 0;
		}
	}
}
/** Latest sample at or before `now`, or undefined when the call had not started. */
function sampleAt(samples, now) {
	let best;
	for (const s of samples) if (s.t <= now) best = s;
	else break;
	return best;
}
/** Track stats at the playhead for one track identity. */
function trackStatsAt(samples, now, media, direction, trackId) {
	const s = sampleAt(samples, now);
	if (!s) return void 0;
	return (s.tracks ?? []).find((t) => t.media === media && t.direction === direction && (trackId == null || t.trackId == null || t.trackId === trackId));
}
function deriveMedia(input, serverCalls = []) {
	const byPc = /* @__PURE__ */ new Map();
	const sessionEvents = [];
	for (const te of input.events) {
		const e = te.event;
		if (e.type === "webrtc") {
			const key = `${te.seq}:${te.idx}`;
			if (!e.pc) {
				const d = describeMarker(e);
				sessionEvents.push({
					key,
					t: te.t,
					kind: e.kind,
					label: d.label,
					severity: d.severity,
					future: te.t > input.now,
					event: e
				});
				continue;
			}
			let g = byPc.get(e.pc);
			if (!g) byPc.set(e.pc, g = {
				events: [],
				samples: []
			});
			g.events.push({
				e,
				t: te.t,
				key
			});
		} else if (e.type === "webrtc_stats") {
			let g = byPc.get(e.pc);
			if (!g) byPc.set(e.pc, g = {
				events: [],
				samples: []
			});
			g.samples.push({
				...e,
				t: te.t
			});
		}
	}
	const summaryByPc = new Map(serverCalls.map((c) => [c.pc, c]));
	const calls = [];
	for (const [pc, g] of byPc) {
		g.events.sort((a, b) => a.t - b.t);
		g.samples.sort((a, b) => a.t - b.t);
		const summary = summaryByPc.get(pc);
		const markers = g.events.map(({ e, t, key }) => {
			const d = describeMarker(e);
			return {
				key,
				t,
				kind: e.kind,
				label: d.label,
				severity: d.severity,
				future: t > input.now,
				event: e
			};
		});
		const created = g.events.find((x) => x.e.kind === "pc_created");
		const firstConnected = g.events.find((x) => x.e.kind === "connection_state" && x.e.state === "connected");
		const closed = g.events.find((x) => x.e.kind === "pc_closed");
		const startedAt = created?.t ?? g.events[0]?.t ?? g.samples[0]?.t ?? input.startTs;
		const endedAt = closed?.t;
		const lastSignal = Math.max(g.events[g.events.length - 1]?.t ?? startedAt, g.samples[g.samples.length - 1]?.t ?? startedAt);
		const durationMs = (endedAt ?? Math.max(lastSignal, input.now)) - startedAt;
		const setupMs = firstConnected ? firstConnected.t - startedAt : void 0;
		const failedToConnect = !firstConnected && (!!closed || g.events.some((x) => x.e.kind === "connection_state" && x.e.state === "failed"));
		let state = "new";
		let iceState;
		let hasTurn;
		let relayed = summary?.relayed ?? false;
		let localCandidate;
		let remoteCandidate;
		let networkType = summary?.networkType;
		let iceRestarts = 0;
		let candidatePairChanges = 0;
		let stateAt = -Infinity;
		let iceAt = -Infinity;
		let pathAt = -Infinity;
		const mediaErrors = [];
		for (const { e, t } of g.events) {
			if (e.kind === "pc_created") hasTurn = e.hasTurn;
			if (t > input.now) continue;
			if (e.kind === "connection_state") {
				state = e.state;
				stateAt = t;
			} else if (e.kind === "ice_state") {
				iceState = e.state;
				iceAt = t;
			} else if (e.kind === "candidate_pair_change") {
				candidatePairChanges++;
				localCandidate = e.localType;
				remoteCandidate = e.remoteType;
				relayed = e.relayed;
				networkType = e.networkType ?? networkType;
				pathAt = t;
			} else if (e.kind === "ice_restart") iceRestarts++;
			else if (e.kind === "pc_closed") {
				state = "closed";
				stateAt = t;
			} else if (e.kind === "media_error") mediaErrors.push({
				t,
				error: e.error,
				source: e.source,
				name: e.name
			});
		}
		const cur = sampleAt(g.samples, input.now);
		if (cur) {
			if (cur.t >= stateAt && cur.connectionState) state = cur.connectionState;
			if (cur.t >= iceAt && cur.iceState) iceState = cur.iceState;
			if (cur.t >= pathAt) {
				localCandidate = cur.localCandidate ?? localCandidate;
				remoteCandidate = cur.remoteCandidate ?? remoteCandidate;
				if (cur.relayed != null) relayed = cur.relayed;
			}
			networkType = cur.networkType ?? networkType;
		}
		const lanes = LANE_ORDER.map((id) => {
			const spec = LANE_SPEC[id];
			const points = [];
			for (const s of g.samples) {
				const v = laneValue(id, s);
				if (typeof v === "number" && Number.isFinite(v)) points.push({
					t: s.t,
					v
				});
			}
			const maxSeen = points.reduce((a, p) => Math.max(a, p.v), 0);
			let current;
			for (const p of points) if (p.t <= input.now) current = p.v;
			else break;
			return {
				id,
				label: spec.label,
				unit: spec.unit,
				colorVar: spec.colorVar,
				points,
				max: Math.max(spec.floor, maxSeen),
				current,
				higherIsWorse: spec.higherIsWorse
			};
		}).filter((l) => l.points.length > 0);
		const freezes = [];
		for (const s of g.samples) {
			const ms = (s.tracks ?? []).filter((t) => t.media === "video" && (t.freezeMs ?? 0) > 0).reduce((a, t) => a + (t.freezeMs ?? 0), 0);
			if (ms <= 0) continue;
			const end = s.t;
			const start = Math.max(s.t - (s.windowMs || 0), end - ms);
			freezes.push({
				start,
				end,
				ms,
				fromMute: false
			});
		}
		const openMutes = /* @__PURE__ */ new Map();
		for (const { e, t } of g.events) if (e.kind === "track_muted" && e.media === "video") openMutes.set(e.trackId, t);
		else if (e.kind === "track_unmuted" && e.media === "video") {
			const start = openMutes.get(e.trackId);
			if (start != null) {
				freezes.push({
					start,
					end: t,
					ms: t - start,
					fromMute: true
				});
				openMutes.delete(e.trackId);
			}
		}
		for (const [, start] of openMutes) freezes.push({
			start,
			end: Math.max(input.now, start),
			ms: Math.max(0, input.now - start),
			fromMute: true
		});
		freezes.sort((a, b) => a.start - b.start);
		const merged = [];
		for (const f of freezes) {
			const prev = merged[merged.length - 1];
			if (prev && f.start <= prev.end) {
				prev.end = Math.max(prev.end, f.end);
				prev.ms = prev.end - prev.start;
				prev.fromMute = prev.fromMute || f.fromMute;
			} else merged.push({ ...f });
		}
		freezes.length = 0;
		freezes.push(...merged);
		const frozenNow = freezes.find((f) => input.now >= f.start && input.now <= f.end);
		const trackKeys = /* @__PURE__ */ new Map();
		const codecs = /* @__PURE__ */ new Map();
		const resolutions = /* @__PURE__ */ new Map();
		for (const { e, t } of g.events) {
			if (e.kind === "codec_change") codecs.set(`${e.media}:${e.direction}`, e.mimeType);
			if (e.kind === "resolution_change" && t <= input.now) resolutions.set(e.trackId, `${e.w}×${e.h}`);
			if (e.kind !== "track_added") continue;
			const key = `${e.media}:${e.direction}:${e.trackId}`;
			trackKeys.set(key, {
				trackId: e.trackId,
				media: e.media,
				direction: e.direction,
				label: e.label,
				muted: false,
				enabled: true,
				ended: false
			});
		}
		for (const s of g.samples) for (const t of s.tracks ?? []) {
			const key = `${t.media}:${t.direction}:${t.trackId ?? "-"}`;
			if (!trackKeys.has(key) && ![...trackKeys.values()].some((r) => r.media === t.media && r.direction === t.direction)) trackKeys.set(key, {
				trackId: t.trackId,
				media: t.media,
				direction: t.direction,
				muted: false,
				enabled: true,
				ended: false
			});
		}
		for (const { e, t } of g.events) {
			if (t > input.now) continue;
			if (e.kind === "track_muted" || e.kind === "track_unmuted" || e.kind === "track_ended" || e.kind === "track_enabled" || e.kind === "track_device_change") for (const row of trackKeys.values()) {
				if (row.media !== e.media || row.direction !== e.direction) continue;
				if (row.trackId && e.trackId && row.trackId !== e.trackId) continue;
				if (e.kind === "track_muted") row.muted = true;
				else if (e.kind === "track_unmuted") row.muted = false;
				else if (e.kind === "track_ended") row.ended = true;
				else if (e.kind === "track_enabled") row.enabled = e.enabled;
				else if (e.kind === "track_device_change" && e.label) row.label = e.label;
			}
		}
		const tracks = [...trackKeys.values()].map((row) => {
			const current = trackStatsAt(g.samples, input.now, row.media, row.direction, row.trackId);
			const res = current?.w && current?.h ? `${current.w}×${current.h}` : row.trackId ? resolutions.get(row.trackId) : void 0;
			return {
				...row,
				codec: current?.codec ?? codecs.get(`${row.media}:${row.direction}`),
				resolution: res,
				current
			};
		});
		tracks.sort((a, b) => a.media === b.media ? a.direction.localeCompare(b.direction) : a.media.localeCompare(b.media));
		const elapsed = Math.max(1, Math.min(input.now, endedAt ?? input.now) - startedAt);
		const freezeSoFar = freezes.filter((f) => f.start <= input.now).reduce((a, f) => a + Math.min(f.ms, Math.max(0, input.now - f.start)), 0);
		const concealment = cur ? Math.max(0, ...(cur.tracks ?? []).filter((t) => t.media === "audio").map((t) => t.concealmentPct ?? 0)) : void 0;
		const qualityScore = cur ? callQualityScore({
			rttMs: cur.rttMs,
			lossPct: lanes.find((l) => l.id === "loss")?.current,
			jitterMs: lanes.find((l) => l.id === "jitter")?.current,
			freezeMs: freezeSoFar,
			durationMs: elapsed,
			concealmentPct: concealment
		}) : void 0;
		calls.push({
			pc,
			startedAt,
			endedAt,
			durationMs,
			state,
			iceState,
			setupMs: summary?.setupMs ?? setupMs,
			failedToConnect: summary?.failedToConnect ?? failedToConnect,
			relayed,
			localCandidate,
			remoteCandidate,
			networkType,
			hasTurn,
			qualityScore,
			callQualityScore: summary?.qualityScore,
			lanes,
			freezes,
			markers,
			tracks,
			samples: g.samples,
			iceRestarts: summary?.iceRestarts ?? iceRestarts,
			candidatePairChanges: summary?.candidatePairChanges ?? candidatePairChanges,
			mediaErrors,
			summary,
			frozenNow,
			future: startedAt > input.now
		});
	}
	calls.sort((a, b) => a.startedAt - b.startedAt);
	sessionEvents.sort((a, b) => a.t - b.t);
	return {
		calls,
		sessionEvents,
		empty: calls.length === 0 && sessionEvents.length === 0
	};
}
/** Worst (lowest) quality score across the calls, for the dock badge. */
function worstQuality(m) {
	const scores = m.calls.map((c) => c.callQualityScore ?? c.qualityScore).filter((v) => typeof v === "number");
	return scores.length ? Math.min(...scores) : void 0;
}
/** Status bucket for a 0..100 quality score. Status colors ship with a label, never color alone. */
function qualityBucket(score) {
	if (score == null || !Number.isFinite(score)) return {
		id: "unknown",
		label: "no data",
		colorVar: "--viz-muted"
	};
	if (score >= 85) return {
		id: "good",
		label: "good",
		colorVar: "--viz-good"
	};
	if (score >= 70) return {
		id: "fair",
		label: "fair",
		colorVar: "--viz-warning"
	};
	if (score >= 50) return {
		id: "poor",
		label: "poor",
		colorVar: "--viz-serious"
	};
	return {
		id: "bad",
		label: "bad",
		colorVar: "--viz-critical"
	};
}
/** Counts for the dock badge and the panel header. */
function mediaCounts(m) {
	return {
		calls: m.calls.length,
		failed: m.calls.filter((c) => c.failedToConnect).length,
		freezes: m.calls.reduce((a, c) => a + Math.max(c.freezes.length, c.summary?.freezeCount ?? 0), 0),
		mediaErrors: m.calls.reduce((a, c) => a + c.mediaErrors.length, 0) + m.sessionEvents.filter((e) => e.kind === "media_error").length
	};
}
//#endregion
//#region src/panels/performance.ts
var VITAL_THRESHOLDS = {
	LCP: [2500, 4e3],
	CLS: [.1, .25],
	INP: [200, 500],
	FCP: [1800, 3e3],
	TTFB: [800, 1800]
};
function rateVital(name, value) {
	const [good, poor] = VITAL_THRESHOLDS[name];
	return value <= good ? "good" : value <= poor ? "needs-improvement" : "poor";
}
function derivePerformance(input, bucketMs = 250) {
	const latest = /* @__PURE__ */ new Map();
	const longTasks = [];
	const memory = [];
	const bucketCount = Math.max(1, Math.ceil((input.endTs - input.startTs) / bucketMs));
	const buckets = Array.from({ length: Math.min(bucketCount, 2e4) }, (_, i) => ({
		t: input.startTs + i * bucketMs,
		end: input.startTs + (i + 1) * bucketMs,
		events: 0,
		mutations: 0,
		busy: 0
	}));
	for (const te of input.events) {
		const e = te.event;
		const b = buckets[Math.min(buckets.length - 1, Math.max(0, Math.floor((te.t - input.startTs) / bucketMs)))];
		if (b) {
			b.events++;
			if (e.type === "mutation") b.mutations += (e.adds?.length ?? 0) + (e.removes?.length ?? 0) + (e.attrs?.length ?? 0) + (e.texts?.length ?? 0);
		}
		if (e.type === "vital") latest.set(e.name, {
			name: e.name,
			value: e.value,
			rating: e.rating ?? rateVital(e.name, e.value),
			unit: e.name === "CLS" ? "score" : "ms",
			t: te.t,
			nodeId: e.id,
			sources: e.sources,
			future: te.t > input.now
		});
		else if (e.type === "longtask") longTasks.push({
			t: te.t,
			end: te.t + e.durationMs,
			durationMs: e.durationMs,
			self: !!e.self,
			future: te.t > input.now
		});
		else if (e.type === "memory") memory.push({
			t: te.t,
			jsHeapUsedMB: e.jsHeapUsedMB,
			jsHeapLimitMB: e.jsHeapLimitMB,
			recorderUsedMB: e.recorderUsedMB,
			recorderBudgetMB: e.recorderBudgetMB,
			recorderPressure: e.recorderPressure,
			pressureState: e.pressureState,
			cpuState: e.cpuState
		});
		else if (e.type === "custom" && e.name === "backstory.memory" && e.props && typeof e.props.usedMB === "number") memory.push({
			t: te.t,
			jsHeapUsedMB: e.props.usedMB
		});
	}
	for (const b of buckets) b.busy = Math.min(1, (b.events + b.mutations * .25) / 40);
	return {
		vitals: [
			"LCP",
			"INP",
			"CLS",
			"FCP",
			"TTFB"
		].map((n) => latest.get(n)).filter((v) => !!v),
		longTasks,
		activity: buckets,
		memory,
		totals: {
			longTaskMs: longTasks.reduce((a, l) => a + l.durationMs, 0),
			longTasks: longTasks.length,
			selfLongTasks: longTasks.filter((l) => l.self).length
		}
	};
}
function formatVital(v) {
	return v.unit === "score" ? v.value.toFixed(3) : `${Math.round(v.value)} ms`;
}
//#endregion
//#region src/panels/state.ts
function deriveState(input, storeFilter) {
	const entries = [];
	const stores = /* @__PURE__ */ new Set();
	for (const te of input.events) {
		const e = te.event;
		if (e.type !== "state") continue;
		const storeName = e.storeName ?? e.store;
		stores.add(storeName);
		if (storeFilter && storeName !== storeFilter) continue;
		entries.push({
			key: `${te.seq}:${te.idx}`,
			t: te.t,
			store: e.store,
			storeName,
			action: e.action,
			diff: e.diff,
			hasSnapshot: e.snapshot !== void 0,
			truncated: e.truncated,
			future: false,
			current: false
		});
	}
	let last = -1;
	entries.forEach((en, i) => {
		en.future = en.t > input.now;
		if (!en.future) last = i;
	});
	if (last >= 0) entries[last].current = true;
	return {
		entries,
		stores: [...stores].sort()
	};
}
/** Reconstruct the store snapshot at entry `key`: last snapshot ≤ key, then apply diffs forward. */
function reconstructState(input, storeName, key) {
	let snapshot = void 0;
	let found = false;
	for (const te of input.events) {
		const e = te.event;
		if (e.type !== "state") continue;
		if ((e.storeName ?? e.store) !== storeName) continue;
		if (e.snapshot !== void 0) snapshot = structuredClone(e.snapshot);
		else if (e.diff && snapshot !== void 0) snapshot = applyPatch(snapshot, e.diff);
		else if (e.diff && snapshot === void 0) snapshot = applyPatch({}, e.diff);
		if (`${te.seq}:${te.idx}` === key) {
			found = true;
			break;
		}
	}
	return found ? snapshot : void 0;
}
/** RFC 6902 subset: add / remove / replace with "/a/b/0" pointers ("-" appends). */
function applyPatch(doc, ops) {
	let root = structuredClone(doc);
	for (const op of ops) {
		const segs = op.path === "" ? [] : op.path.split("/").slice(1).map(unescape);
		if (segs.length === 0) {
			root = op.op === "remove" ? void 0 : structuredClone(op.value);
			continue;
		}
		if (root === void 0 || root === null || typeof root !== "object") root = /^\d+$|^-$/.test(segs[0]) ? [] : {};
		let parent = root;
		for (let i = 0; i < segs.length - 1; i++) {
			const s = segs[i];
			const p = parent;
			const next = Array.isArray(p) ? p[Number(s)] : p[s];
			if (next === void 0 || next === null || typeof next !== "object") {
				const created = /^\d+$|^-$/.test(segs[i + 1]) ? [] : {};
				if (Array.isArray(p)) p[Number(s)] = created;
				else p[s] = created;
				parent = created;
			} else parent = next;
		}
		const last = segs[segs.length - 1];
		const p = parent;
		if (Array.isArray(p)) {
			const idx = last === "-" ? p.length : Number(last);
			if (op.op === "add") p.splice(idx, 0, structuredClone(op.value));
			else if (op.op === "remove") p.splice(idx, 1);
			else p[idx] = structuredClone(op.value);
		} else if (op.op === "remove") delete p[last];
		else p[last] = structuredClone(op.value);
	}
	return root;
}
function unescape(s) {
	return s.replace(/~1/g, "/").replace(/~0/g, "~");
}
function isReactTree(v) {
	return !!v && typeof v === "object" && typeof v.name === "string";
}
/** Summarize a diff for list display: "+2 −1 ~3". */
function summarizeDiff(diff) {
	if (!diff?.length) return "";
	let add = 0;
	let rem = 0;
	let rep = 0;
	for (const d of diff) if (d.op === "add") add++;
	else if (d.op === "remove") rem++;
	else rep++;
	return [
		add && `+${add}`,
		rem && `−${rem}`,
		rep && `~${rep}`
	].filter(Boolean).join(" ");
}
//#endregion
//#region src/panels/application.ts
/** Latest facts per kind at or before the playhead; routes list includes the whole session. */
function deriveApplication(input, meta) {
	const m = {
		storage: {
			keys: [],
			values: {}
		},
		cookies: { keys: [] },
		serviceWorker: {},
		flags: [],
		experiments: [],
		env: {},
		routes: [],
		device: []
	};
	const flagMap = /* @__PURE__ */ new Map();
	const expMap = /* @__PURE__ */ new Map();
	for (const te of input.events) {
		const e = te.event;
		if (e.type === "navigation") {
			m.routes.push({
				t: te.t,
				url: e.url,
				title: e.title,
				route: e.route,
				kind: e.kind,
				future: te.t > input.now,
				current: false
			});
			continue;
		}
		if (e.type === "snapshot" && m.routes.length === 0) {
			m.routes.push({
				t: te.t,
				url: e.url,
				title: e.title,
				kind: "load",
				future: te.t > input.now,
				current: false
			});
			continue;
		}
		if (e.type !== "application" || te.t > input.now) continue;
		switch (e.kind) {
			case "storage":
				m.storage = {
					keys: e.keys ?? [],
					values: e.values ?? {},
					t: te.t
				};
				break;
			case "cookies":
				m.cookies = {
					keys: e.keys ?? [],
					t: te.t
				};
				break;
			case "service_worker":
				m.serviceWorker = {
					state: e.swState,
					t: te.t
				};
				break;
			case "flags":
				for (const [k, v] of Object.entries(e.flags ?? {})) flagMap.set(k, {
					value: v,
					provider: e.provider
				});
				break;
			case "experiments":
				for (const [k, v] of Object.entries(e.flags ?? {})) expMap.set(k, {
					value: v,
					provider: e.provider
				});
				break;
			case "env": Object.assign(m.env, e.values ?? {});
		}
	}
	m.flags = [...flagMap.entries()].map(([key, v]) => ({
		key,
		...v
	})).sort((a, b) => a.key.localeCompare(b.key));
	m.experiments = [...expMap.entries()].map(([key, v]) => ({
		key,
		...v
	})).sort((a, b) => a.key.localeCompare(b.key));
	let last = -1;
	m.routes.forEach((r, i) => {
		if (!r.future) last = i;
	});
	if (last >= 0) m.routes[last].current = true;
	if (meta) {
		const ua = parseUA(meta.userAgent);
		m.device = [
			["Browser", ua.browser],
			["OS", ua.os],
			["Device", ua.device],
			["Viewport", `${meta.viewport.w}×${meta.viewport.h} @${meta.viewport.dpr}x`],
			["Language", meta.language ?? "–"],
			["Timezone", meta.timezone ?? "–"],
			["Release", meta.release ?? "–"],
			["Environment", meta.environment ?? "–"],
			["SDK", meta.sdkVersion],
			["Privacy mode", meta.privacyMode],
			["Recording mode", meta.recordingMode],
			["Connection", meta.connection?.effectiveType ?? "–"],
			["Device memory", meta.deviceMemoryGB ? `${meta.deviceMemoryGB} GB` : "–"]
		];
	}
	return m;
}
function parseUA(ua) {
	let browser = "other";
	let m;
	if (m = /Edg\/([\d.]+)/.exec(ua)) browser = `Edge ${m[1].split(".")[0]}`;
	else if (m = /OPR\/([\d.]+)/.exec(ua)) browser = `Opera ${m[1].split(".")[0]}`;
	else if (m = /Chrome\/([\d.]+)/.exec(ua)) browser = `Chrome ${m[1].split(".")[0]}`;
	else if (m = /Firefox\/([\d.]+)/.exec(ua)) browser = `Firefox ${m[1].split(".")[0]}`;
	else if (/Safari\//.test(ua) && (m = /Version\/([\d.]+)/.exec(ua))) browser = `Safari ${m[1].split(".")[0]}`;
	let os = "other";
	if (/Windows NT/.test(ua)) os = "Windows";
	else if (/iPhone|iPad/.test(ua)) os = "iOS";
	else if (/Mac OS X|Macintosh/.test(ua)) os = "macOS";
	else if (/Android/.test(ua)) os = "Android";
	else if (/CrOS/.test(ua)) os = "ChromeOS";
	else if (/Linux/.test(ua)) os = "Linux";
	const device = /iPad|Tablet/.test(ua) ? "tablet" : /Mobi|iPhone|Android.*Mobile/.test(ua) ? "mobile" : "desktop";
	return {
		browser,
		os,
		device
	};
}
//#endregion
//#region src/panels/accessibility.ts
var INTERACTIVE = "a[href], button, input, select, textarea, [role=button], [role=link], [tabindex]";
/**
* Axe-style audit implemented locally (no dependency). Runs against the mirror
* document at the current time. `idOf` maps a live element to its recorded id.
*/
function auditAccessibility(doc, idOf) {
	const issues = [];
	const push = (rule, severity, el, message, help) => {
		issues.push({
			rule,
			severity,
			message,
			help,
			nodeId: idOf(el),
			selector: selectorFor(el),
			label: describeElement(el)
		});
	};
	const all = Array.from(doc.querySelectorAll("*")).filter((el) => !el.closest("[data-backstory-placeholder],[data-backstory-removed]"));
	const visible = (el) => {
		const win = doc.defaultView;
		if (!win) return true;
		const cs = win.getComputedStyle(el);
		return cs.display !== "none" && cs.visibility !== "hidden" && el.getAttribute("aria-hidden") !== "true";
	};
	const html = doc.documentElement;
	if (html && !(html.getAttribute("lang") ?? "").trim()) push("html-has-lang", "serious", html, "<html> has no lang attribute", "Set lang so screen readers pick the right voice.");
	for (const img of Array.from(doc.querySelectorAll("img"))) {
		if (!visible(img)) continue;
		if (img.getAttribute("alt") == null && img.getAttribute("role") !== "presentation" && img.getAttribute("aria-hidden") !== "true") push("image-alt", "critical", img, "Image has no alt attribute", "Add alt text, or alt=\"\" for decorative images.");
	}
	for (const el of Array.from(doc.querySelectorAll("button, [role=button], a[href], [role=link]"))) {
		if (!visible(el)) continue;
		if (!accessibleName(el)) {
			const isLink = el.tagName.toLowerCase() === "a" || el.getAttribute("role") === "link";
			push(isLink ? "link-name" : "button-name", "serious", el, `${isLink ? "Link" : "Button"} has no accessible name`, "Add visible text, aria-label, or aria-labelledby.");
		}
	}
	for (const el of Array.from(doc.querySelectorAll("input, select, textarea"))) {
		if (!visible(el)) continue;
		const type = (el.getAttribute("type") ?? "text").toLowerCase();
		if ([
			"hidden",
			"submit",
			"button",
			"reset",
			"image"
		].includes(type)) continue;
		if (!accessibleName(el)) push("label", "critical", el, "Form control has no label", "Associate a <label for>, wrap in a <label>, or use aria-label.");
	}
	const seen = /* @__PURE__ */ new Map();
	for (const el of all) {
		const id = el.getAttribute("id");
		if (!id) continue;
		if (seen.get(id)) push("duplicate-id", "minor", el, `Duplicate id "${id}"`, "Ids must be unique so labels and aria references resolve.");
		else seen.set(id, el);
	}
	let lastLevel = 0;
	for (const hEl of Array.from(doc.querySelectorAll("h1,h2,h3,h4,h5,h6"))) {
		const level = Number(hEl.tagName[1]);
		if (lastLevel && level > lastLevel + 1) push("heading-order", "moderate", hEl, `Heading level skips from h${lastLevel} to h${level}`, "Do not skip heading levels.");
		lastLevel = level;
	}
	if (!doc.querySelector("h1") && doc.body && doc.body.children.length) push("page-has-heading-one", "moderate", doc.body, "Page has no <h1>", "Give the page one top-level heading.");
	for (const el of Array.from(doc.querySelectorAll("[tabindex]"))) {
		const v = Number(el.getAttribute("tabindex"));
		if (v > 0) push("tabindex", "serious", el, `tabindex="${v}" overrides the natural focus order`, "Use tabindex=\"0\" or -1 and order elements in the DOM.");
	}
	const BOOL = /* @__PURE__ */ new Set([
		"aria-hidden",
		"aria-expanded",
		"aria-pressed",
		"aria-checked",
		"aria-selected",
		"aria-disabled",
		"aria-required",
		"aria-invalid",
		"aria-busy",
		"aria-modal",
		"aria-multiline",
		"aria-readonly"
	]);
	const IDREF = /* @__PURE__ */ new Set([
		"aria-labelledby",
		"aria-describedby",
		"aria-controls",
		"aria-owns",
		"aria-activedescendant"
	]);
	for (const el of all) {
		for (const a of Array.from(el.attributes)) {
			if (!a.name.startsWith("aria-")) continue;
			if (BOOL.has(a.name) && ![
				"true",
				"false",
				"mixed",
				"undefined",
				"grammar",
				"spelling"
			].includes(a.value)) push("aria-valid-attr-value", "serious", el, `${a.name}="${a.value}" is not a valid value`, "Use true/false (or mixed where allowed).");
			else if (IDREF.has(a.name)) {
				const missing = a.value.split(/\s+/).filter((id) => id && !doc.getElementById(id));
				if (missing.length) push("aria-valid-idref", "serious", el, `${a.name} references missing id(s): ${missing.join(", ")}`, "Point aria references at existing elements.");
			}
		}
		const role = el.getAttribute("role");
		if (role && !KNOWN_ROLES.has(role)) push("aria-roles", "serious", el, `role="${role}" is not a known ARIA role`, "Use a valid ARIA role.");
	}
	for (const f of Array.from(doc.querySelectorAll("iframe, [data-backstory-placeholder=iframe]"))) if (!(f.getAttribute("title") ?? "").trim()) push("frame-title", "serious", f, "Frame has no title", "Add a title describing the frame's content.");
	for (const a of Array.from(doc.querySelectorAll("a"))) {
		if (!visible(a)) continue;
		if (!a.getAttribute("href") && !a.getAttribute("role")) push("link-href", "minor", a, "Anchor without href is not keyboard reachable", "Use a <button> for actions or give the link an href.");
	}
	const win = doc.defaultView;
	if (win) {
		let checked = 0;
		for (const el of all) {
			if (checked > 400) break;
			if (!el.childNodes.length || !Array.from(el.childNodes).some((n) => n.nodeType === 3 && (n.textContent ?? "").trim())) continue;
			if (!visible(el)) continue;
			const cs = win.getComputedStyle(el);
			const fg = parseColor(cs.color);
			const bg = effectiveBackground(el, win);
			if (!fg || !bg) continue;
			checked++;
			const ratio = contrastRatio(fg, bg);
			const size = Number.parseFloat(cs.fontSize) || 16;
			const bold = Number(cs.fontWeight) >= 700 || cs.fontWeight === "bold";
			const min = size >= 24 || size >= 18.66 && bold ? 3 : 4.5;
			if (ratio < min) push("color-contrast", "serious", el, `Text contrast ${ratio.toFixed(2)}:1 is below ${min}:1`, "Increase the contrast between text and its background.");
		}
	}
	for (const list of Array.from(doc.querySelectorAll("ul, ol"))) if (Array.from(list.children).filter((c) => ![
		"li",
		"script",
		"template"
	].includes(c.tagName.toLowerCase())).length) push("list", "serious", list, "List contains non-<li> children", "Only <li> elements may be direct children of lists.");
	for (const el of Array.from(doc.querySelectorAll(INTERACTIVE))) {
		const parent = el.parentElement?.closest(INTERACTIVE);
		if (parent && parent !== el) push("nested-interactive", "serious", el, "Interactive element nested inside another interactive element", "Do not nest buttons or links inside each other.");
	}
	const bySeverity = {
		critical: 0,
		serious: 0,
		moderate: 0,
		minor: 0
	};
	for (const i of issues) bySeverity[i.severity]++;
	const order = [
		"critical",
		"serious",
		"moderate",
		"minor"
	];
	issues.sort((a, b) => order.indexOf(a.severity) - order.indexOf(b.severity));
	return {
		issues,
		bySeverity,
		rulesRun: 13,
		nodesChecked: all.length
	};
}
/** Focus order path from `focus` events (keyboard users' actual traversal). */
function focusPath(input) {
	const out = [];
	for (const te of input.events) if (te.event.type === "focus") out.push({
		t: te.t,
		nodeId: te.event.id,
		future: te.t > input.now
	});
	return out;
}
var KNOWN_ROLES = /* @__PURE__ */ new Set([
	"alert",
	"alertdialog",
	"application",
	"article",
	"banner",
	"button",
	"cell",
	"checkbox",
	"columnheader",
	"combobox",
	"complementary",
	"contentinfo",
	"definition",
	"dialog",
	"directory",
	"document",
	"feed",
	"figure",
	"form",
	"grid",
	"gridcell",
	"group",
	"heading",
	"img",
	"link",
	"list",
	"listbox",
	"listitem",
	"log",
	"main",
	"marquee",
	"math",
	"menu",
	"menubar",
	"menuitem",
	"menuitemcheckbox",
	"menuitemradio",
	"navigation",
	"none",
	"note",
	"option",
	"presentation",
	"progressbar",
	"radio",
	"radiogroup",
	"region",
	"row",
	"rowgroup",
	"rowheader",
	"scrollbar",
	"search",
	"searchbox",
	"separator",
	"slider",
	"spinbutton",
	"status",
	"switch",
	"tab",
	"table",
	"tablist",
	"tabpanel",
	"term",
	"textbox",
	"timer",
	"toolbar",
	"tooltip",
	"tree",
	"treegrid",
	"treeitem"
]);
function parseColor(s) {
	const m = /rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)/.exec(s);
	if (m) return [
		Number(m[1]),
		Number(m[2]),
		Number(m[3]),
		m[4] != null ? Number(m[4]) : 1
	];
	const hex = /^#([0-9a-f]{3,8})$/i.exec(s.trim());
	if (hex) {
		let hx = hex[1];
		if (hx.length === 3 || hx.length === 4) hx = hx.split("").map((c) => c + c).join("");
		const n = Number.parseInt(hx.slice(0, 6), 16);
		const a = hx.length === 8 ? Number.parseInt(hx.slice(6, 8), 16) / 255 : 1;
		return [
			n >> 16 & 255,
			n >> 8 & 255,
			n & 255,
			a
		];
	}
	if (s === "transparent") return [
		0,
		0,
		0,
		0
	];
	return null;
}
function effectiveBackground(el, win) {
	let cur = el;
	while (cur) {
		const bg = parseColor(win.getComputedStyle(cur).backgroundColor);
		if (bg && bg[3] > .99) return bg;
		cur = cur.parentElement;
	}
	return [
		255,
		255,
		255,
		1
	];
}
function luminance([r, g, b]) {
	const f = (c) => {
		const v = c / 255;
		return v <= .03928 ? v / 12.92 : Math.pow((v + .055) / 1.055, 2.4);
	};
	return .2126 * f(r) + .7152 * f(g) + .0722 * f(b);
}
function contrastRatio(a, b) {
	const la = luminance(a);
	const lb = luminance(b);
	const [hi, lo] = la > lb ? [la, lb] : [lb, la];
	return (hi + .05) / (lo + .05);
}
//#endregion
//#region src/panels/issues.ts
var SEVERITY_ORDER = [
	"critical",
	"high",
	"medium",
	"low",
	"info"
];
function deriveIssues(input, issues, clusters = []) {
	const byCluster = new Map(clusters.map((c) => [c.id, c]));
	const byKey = /* @__PURE__ */ new Map();
	for (const te of input.events) byKey.set(`${te.seq}:${te.idx}`, {
		t: te.t,
		type: te.event.type
	});
	const rows = issues.map((issue) => {
		const t = input.startTs + issue.tStart;
		const end = input.startTs + Math.max(issue.tStart, issue.tEnd);
		return {
			issue,
			t,
			end,
			future: t > input.now,
			active: input.now >= t && input.now <= end,
			evidence: issue.evidence.map((ev) => {
				const [seq, idx] = ev.split(":").map(Number);
				const hit = byKey.get(ev);
				return {
					seq: seq ?? 0,
					idx: idx ?? 0,
					t: hit?.t,
					type: hit?.type
				};
			}),
			cluster: byCluster.get(issue.clusterId)
		};
	});
	rows.sort((a, b) => a.t - b.t || SEVERITY_ORDER.indexOf(a.issue.severity) - SEVERITY_ORDER.indexOf(b.issue.severity));
	return rows;
}
function issueCounts(rows) {
	const c = {
		critical: 0,
		high: 0,
		medium: 0,
		low: 0,
		info: 0
	};
	for (const r of rows) c[r.issue.severity]++;
	return c;
}
function humanKind(kind) {
	return kind.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}
//#endregion
//#region src/panels/elements.ts
/**
* Elements panel model over the live mirror document at the current time.
* Lazy: callers ask for children of a node id (or the document) on expand.
*/
var ElementsModel = class {
	mirror;
	constructor(mirror) {
		this.mirror = mirror;
	}
	node(id) {
		if (id === void 0) return this.mirror.iframe.contentDocument ?? void 0;
		return this.mirror.getNode(id);
	}
	describe(n, depth) {
		const id = this.mirror.getId(n);
		if (n.nodeType === 9) return {
			nodeId: id,
			kind: "document",
			label: "#document",
			hasChildren: n.childNodes.length > 0,
			childCount: n.childNodes.length,
			masked: false,
			depth
		};
		if (n.nodeType === 3) {
			const text = (n.textContent ?? "").replace(/\s+/g, " ").trim();
			return {
				nodeId: id,
				kind: "text",
				label: "#text",
				text: text.length > 80 ? `${text.slice(0, 80)}…` : text,
				hasChildren: false,
				childCount: 0,
				masked: !!(n.parentElement && n.parentElement.hasAttribute("data-backstory-masked")),
				depth
			};
		}
		if (n.nodeType === 8) return {
			nodeId: id,
			kind: "comment",
			label: `<!-- ${(n.textContent ?? "").slice(0, 40)} -->`,
			hasChildren: false,
			childCount: 0,
			masked: false,
			depth
		};
		if (n.nodeType === 1) {
			const el = n;
			const kids = this.childrenOf(el);
			return {
				nodeId: id,
				kind: "element",
				label: describeElement(el),
				hasChildren: kids.length > 0,
				childCount: kids.length,
				masked: el.hasAttribute("data-backstory-masked"),
				placeholder: el.getAttribute("data-backstory-placeholder") ?? void 0,
				depth
			};
		}
		return {
			nodeId: id,
			kind: "other",
			label: n.nodeName,
			hasChildren: false,
			childCount: 0,
			masked: false,
			depth
		};
	}
	/** Children worth showing (skips whitespace-only text and our own injected head styles). */
	childrenOf(n) {
		const host = n.nodeType === 1 && n.shadowRoot && n.getAttribute("data-backstory-shadow") === "" ? n.shadowRoot : n;
		return Array.from(host.childNodes).filter((c) => {
			if (c.nodeType === 3) return (c.textContent ?? "").trim().length > 0;
			if (c.nodeType === 1) {
				const el = c;
				return !el.hasAttribute("data-backstory-player") && !el.hasAttribute("data-backstory-adopted") && !(el.tagName === "META" && el.getAttribute("http-equiv") === "Content-Security-Policy") && el.tagName !== "BASE";
			}
			return c.nodeType === 8;
		});
	}
	root() {
		const doc = this.mirror.iframe.contentDocument;
		if (!doc || !doc.documentElement) return null;
		return this.describe(doc.documentElement, 0);
	}
	children(nodeId, depth) {
		const n = this.node(nodeId);
		if (!n) return [];
		return this.childrenOf(n).map((c) => this.describe(c, depth));
	}
	/** Ids from the root to the node, for expanding the tree to a selection. */
	pathTo(nodeId) {
		const n = this.node(nodeId);
		if (!n) return [];
		const path = [];
		let cur = n;
		while (cur) {
			const id = this.mirror.getId(cur);
			if (id !== void 0 && cur.nodeType !== 9) path.unshift(id);
			cur = cur.parentNode ?? cur.host ?? null;
		}
		return path;
	}
	details(nodeId) {
		const n = this.node(nodeId);
		if (!n || n.nodeType !== 1) return null;
		const el = n;
		return {
			nodeId,
			label: describeElement(el),
			selector: selectorFor(el),
			xpath: xpathFor(el),
			testId: el.getAttribute("data-testid") ?? void 0,
			attributes: attributesOf(el),
			computed: computedStylesOf(el),
			box: boxModelOf(el),
			breadcrumb: [...ancestorsOf(el), el].filter((a) => a.nodeType === 1).map((a) => ({
				nodeId: this.mirror.getId(a),
				label: describeElement(a)
			}))
		};
	}
	/** Count of mapped nodes. */
	get size() {
		return this.mirror.size;
	}
};
//#endregion
//#region src/panels/render.ts
var timeCell = (ctx, t) => h("span", {
	class: "t",
	text: fmtClock(ctx ? rel(ctx.input, t) : 0)
});
function serverLevel(severity) {
	const s = severity.toLowerCase();
	if (s.startsWith("err") || s.startsWith("fatal") || s.startsWith("crit") || s.startsWith("panic")) return "error";
	if (s.startsWith("warn")) return "warn";
	if (s.startsWith("debug") || s.startsWith("trace")) return "debug";
	return "info";
}
/**
* One time-ordered list of backend log lines and error spans. An error span
* that already has an error log line in it is not repeated.
*/
function serverRows(traces, logs) {
	const rows = [];
	const spanNames = /* @__PURE__ */ new Map();
	for (const t of traces) for (const s of t.spans) spanNames.set(s.spanId, s.name);
	const loggedErrorSpans = /* @__PURE__ */ new Set();
	logs.forEach((l, i) => {
		const level = serverLevel(l.severity);
		if (level === "error" && l.spanId) loggedErrorSpans.add(l.spanId);
		rows.push({
			key: `l${i}`,
			ts: l.ts,
			level,
			service: l.service,
			span: l.spanId ? spanNames.get(l.spanId) : void 0,
			text: l.exceptionType && !l.body.includes(l.exceptionType) ? `${l.exceptionType}: ${l.body}` : l.body,
			stack: l.exceptionStack || void 0
		});
	});
	for (const t of traces) for (const s of t.spans) {
		if (s.status !== "error" || loggedErrorSpans.has(s.spanId)) continue;
		const a = s.attributes;
		const msg = String(a["exception.message"] ?? a["error.message"] ?? (a["http.status_code"] ? `responded ${a["http.status_code"]}` : "failed"));
		const type = a["exception.type"] ? `${String(a["exception.type"])}: ` : "";
		rows.push({
			key: `s${s.spanId}`,
			ts: s.startMs,
			level: "error",
			service: s.service,
			span: s.name,
			text: type + msg,
			stack: a["exception.stacktrace"] ? String(a["exception.stacktrace"]) : void 0
		});
	}
	return rows.sort((a, b) => a.ts - b.ts);
}
function createConsolePanel(container) {
	const levels = /* @__PURE__ */ new Set([
		"log",
		"info",
		"warn",
		"error",
		"debug",
		"trace",
		"exception"
	]);
	let text = "";
	let tab = "browser";
	let symbolicator = null;
	let symbolicated = /* @__PURE__ */ new Map();
	let traces = null;
	let serverLogs = null;
	let serverText = "";
	const serverLevels = /* @__PURE__ */ new Set([
		"error",
		"warn",
		"info",
		"debug"
	]);
	let ctx = null;
	const expanded = /* @__PURE__ */ new Set();
	const root = h("div", { class: "backstory-player backstory-player-console" });
	container.appendChild(root);
	const draw = () => {
		if (!ctx) return;
		clear(root);
		const entries = deriveConsole(ctx.input, {
			levels,
			text
		});
		const counts = consoleCounts(deriveConsole(ctx.input));
		root.appendChild(h("div", {
			class: "backstory-player-tabs",
			role: "tablist"
		}, h("button", {
			class: "backstory-player-tab",
			role: "tab",
			"aria-selected": tab === "browser",
			text: "Browser",
			onclick: () => {
				tab = "browser";
				draw();
			}
		}), h("button", {
			class: "backstory-player-tab",
			role: "tab",
			"aria-selected": tab === "server",
			text: "Server",
			onclick: () => {
				tab = "server";
				draw();
				loadTraces();
			}
		})));
		if (tab === "server") {
			drawServer();
			return;
		}
		const bar = h("div", { class: "backstory-player-toolbar" });
		const search = h("input", {
			type: "search",
			placeholder: "Filter console",
			value: text,
			"aria-label": "Filter console"
		});
		search.addEventListener("input", () => {
			text = search.value;
			draw();
		});
		bar.appendChild(search);
		for (const lv of [
			"exception",
			"error",
			"warn",
			"info",
			"log",
			"debug"
		]) {
			const n = lv === "log" ? counts.log + counts.trace : counts[lv];
			bar.appendChild(h("button", {
				class: `backstory-player-chip ${lv === "error" || lv === "exception" ? "danger" : ""}`,
				"aria-pressed": levels.has(lv),
				text: `${lv}${n ? ` ${n}` : ""}`,
				onclick: () => {
					if (levels.has(lv)) {
						levels.delete(lv);
						if (lv === "log") levels.delete("trace");
					} else {
						levels.add(lv);
						if (lv === "log") levels.add("trace");
					}
					draw();
				}
			}));
		}
		root.appendChild(bar);
		const body = h("div", {
			class: "backstory-player-body",
			role: "log"
		});
		if (!entries.length) {
			const total = Object.values(counts).reduce((a, b) => a + b, 0);
			body.appendChild(h("div", {
				class: "backstory-player-empty",
				text: total ? "No console output matches this filter." : "No console output in this session. Browser-generated messages (WebGL, CSP, blocked resources) never pass through console.* and cannot be captured."
			}));
		}
		for (const en of entries) body.appendChild(renderEntry(en));
		root.appendChild(body);
		body.querySelector("[aria-current=\"true\"]")?.scrollIntoView?.({ block: "nearest" });
	};
	const renderEntry = (en) => {
		const c = ctx;
		const detail = h("div", {});
		detail.appendChild(h("span", {
			class: `backstory-player-level ${en.level}`,
			text: en.level === "exception" ? "✕" : en.level === "warn" ? "▲" : en.level === "error" ? "!" : "›"
		}));
		en.args.forEach((a, i) => {
			const parsed = en.parsed[i];
			if (parsed !== null && parsed !== void 0) {
				const d = h("details", {
					class: "backstory-player-obj",
					open: expanded.has(`${en.key}:${i}`)
				});
				d.addEventListener("toggle", () => {
					if (d.open) expanded.add(`${en.key}:${i}`);
					else expanded.delete(`${en.key}:${i}`);
				});
				d.appendChild(h("summary", { text: summarizeValue(parsed) }));
				d.appendChild(h("pre", {
					class: "backstory-player-pre",
					text: JSON.stringify(parsed, null, 2)
				}));
				detail.appendChild(d);
			} else detail.appendChild(h("span", { text: (i ? " " : "") + a }));
		});
		if (en.source?.file) detail.appendChild(h("div", {
			class: "backstory-player-muted backstory-player-mono",
			text: `${en.source.file}:${en.source.line ?? ""}${en.source.column ? `:${en.source.column}` : ""}`
		}));
		if (en.frames.length) {
			const list = h("ul", { class: "backstory-player-frames" });
			const sym = symbolicated.get(en.key);
			en.frames.forEach((f, i) => list.appendChild(renderFrame(f, sym?.[i] ?? null)));
			const d = h("details", { open: en.level === "exception" && expanded.has(`${en.key}:stack`) });
			d.addEventListener("toggle", () => {
				if (d.open) {
					expanded.add(`${en.key}:stack`);
					symbolicate(en);
				} else expanded.delete(`${en.key}:stack`);
			});
			d.appendChild(h("summary", {
				class: "backstory-player-muted",
				text: `${en.frames.length} frame${en.frames.length === 1 ? "" : "s"}${sym ? " · symbolicated" : symbolicator?.isUnavailable ? " · source maps unavailable" : ""}`
			}));
			d.appendChild(list);
			detail.appendChild(d);
		}
		return h("div", {
			class: `backstory-player-row ${en.future ? "future" : ""}`,
			"aria-current": en.current ? "true" : void 0,
			"data-key": en.key,
			onclick: (e) => {
				if (e.target.closest("details,a")) return;
				c.actions.seek(en.t);
			}
		}, timeCell(c, en.t), detail);
	};
	const renderFrame = (f, s) => {
		const li = h("li", { class: s?.inApp ? "inapp" : "" });
		const fn = s?.originalFunction ?? f.function ?? "(anonymous)";
		li.appendChild(h("span", { text: `${fn} ` }));
		if (s?.originalFile) li.appendChild(h("span", {
			class: "orig",
			text: `${s.originalFile}:${s.originalLine ?? ""}:${s.originalColumn ?? ""}`
		}));
		else li.appendChild(h("span", {
			class: "backstory-player-muted",
			text: `${f.file ?? ""}:${f.line ?? ""}:${f.column ?? ""}`
		}));
		if (s?.context) {
			const a = h("a", { text: " view source" });
			const ctxEl = h("div", {
				class: "backstory-player-ctx",
				style: "display:none"
			});
			const pre = s.context.pre.join("\n");
			const post = s.context.post.join("\n");
			ctxEl.appendChild(h("span", { text: pre + (pre ? "\n" : "") }));
			ctxEl.appendChild(h("span", {
				class: "cur",
				text: s.context.line
			}));
			ctxEl.appendChild(h("span", { text: (post ? "\n" : "") + post }));
			a.addEventListener("click", (e) => {
				e.stopPropagation();
				ctxEl.style.display = ctxEl.style.display === "none" ? "block" : "none";
			});
			li.appendChild(a);
			li.appendChild(ctxEl);
		}
		return li;
	};
	const symbolicate = async (en) => {
		if (!symbolicator || symbolicated.has(en.key) || !en.frames.length) return;
		const res = await symbolicator.symbolicate(en.frames);
		if (res.some((r) => r)) {
			symbolicated.set(en.key, res);
			draw();
		}
	};
	const loadTraces = async () => {
		if (traces || !ctx?.fetchers || !ctx.fetchers.traces && !ctx.fetchers.logs) return;
		const f = ctx.fetchers;
		const [t, l] = await Promise.all([f.traces ? f.traces().catch(() => []) : Promise.resolve([]), f.logs ? f.logs().catch(() => []) : Promise.resolve([])]);
		traces = t;
		serverLogs = l;
		draw();
	};
	const drawServer = () => {
		const c = ctx;
		const f = c.fetchers;
		const body = h("div", {
			class: "backstory-player-body",
			role: "log"
		});
		if (!traces) {
			body.appendChild(h("div", {
				class: "backstory-player-empty",
				text: f?.traces || f?.logs ? "Loading server logs…" : "Connect your backend to see server logs next to this replay."
			}));
			root.appendChild(body);
			return;
		}
		const rows = serverRows(traces, serverLogs ?? []);
		const counts = {
			error: 0,
			warn: 0,
			info: 0,
			debug: 0
		};
		for (const r of rows) counts[r.level]++;
		const bar = h("div", { class: "backstory-player-toolbar" });
		const search = h("input", {
			type: "search",
			placeholder: "Filter server logs",
			value: serverText,
			"aria-label": "Filter server logs"
		});
		search.addEventListener("input", () => {
			serverText = search.value;
			draw();
		});
		bar.appendChild(search);
		for (const lv of [
			"error",
			"warn",
			"info",
			"debug"
		]) bar.appendChild(h("button", {
			class: `backstory-player-chip ${lv === "error" ? "danger" : ""}`,
			"aria-pressed": serverLevels.has(lv),
			text: `${lv}${counts[lv] ? ` ${counts[lv]}` : ""}`,
			onclick: () => {
				if (serverLevels.has(lv)) serverLevels.delete(lv);
				else serverLevels.add(lv);
				draw();
			}
		}));
		root.appendChild(bar);
		const q = serverText.trim().toLowerCase();
		const shown = rows.filter((r) => serverLevels.has(r.level) && (!q || r.text.toLowerCase().includes(q) || r.service.toLowerCase().includes(q)));
		if (!shown.length) body.appendChild(h("div", {
			class: "backstory-player-empty",
			text: rows.length ? "No server logs match this filter." : "No server logs or backend errors are linked to this session yet."
		}));
		let current = null;
		for (const r of shown) {
			const row = h("div", {
				class: "backstory-player-row",
				onclick: () => c.actions.seek(r.ts)
			}, timeCell(c, r.ts), h("div", {}, h("span", {
				class: `backstory-player-level ${r.level}`,
				text: r.level === "error" ? "!" : r.level === "warn" ? "▲" : "›"
			}), h("span", {
				class: "backstory-player-mono backstory-player-muted",
				text: `${r.service}${r.span ? ` · ${r.span}` : ""} `
			}), h("span", { text: r.text }), r.stack ? h("details", {}, h("summary", {
				class: "backstory-player-muted",
				text: "Stack"
			}), h("pre", {
				class: "backstory-player-pre",
				text: r.stack
			})) : null));
			if (r.ts <= c.input.now) current = row;
			body.appendChild(row);
		}
		current?.setAttribute("aria-current", "true");
		root.appendChild(body);
		current?.scrollIntoView?.({ block: "nearest" });
	};
	return {
		update(next) {
			const releaseChanged = ctx?.release !== next.release || ctx?.fetchers?.symbolicate !== next.fetchers?.symbolicate;
			ctx = next;
			if (!symbolicator || releaseChanged) {
				symbolicator = new Symbolicator(next.fetchers?.symbolicate, next.release ?? next.meta?.release);
				symbolicated = /* @__PURE__ */ new Map();
			}
			draw();
			for (const en of deriveConsole(next.input)) if (en.level === "exception" && en.frames.length) symbolicate(en);
		},
		destroy() {
			root.remove();
		}
	};
}
function summarizeValue(v) {
	if (Array.isArray(v)) return `Array(${v.length})`;
	if (v && typeof v === "object") {
		const keys = Object.keys(v);
		return `{${keys.slice(0, 4).join(", ")}${keys.length > 4 ? ", …" : ""}}`;
	}
	return String(v);
}
var CUSTOM_SEVERITIES = [
	"error",
	"warn",
	"info",
	"debug"
];
function createCustomPanel(container) {
	let ctx = null;
	let text = "";
	const levels = new Set(CUSTOM_SEVERITIES);
	const root = h("div", { class: "backstory-player backstory-player-custom" });
	container.appendChild(root);
	const draw = () => {
		if (!ctx) return;
		clear(root);
		const all = ctx.input.events.filter((row) => row.event.type === "custom" && !row.event.name.startsWith("backstory."));
		const counts = Object.fromEntries(CUSTOM_SEVERITIES.map((level) => [level, all.filter((row) => (row.event.type === "custom" ? row.event.severity ?? "info" : "info") === level).length]));
		const query = text.trim().toLowerCase();
		const rows = all.filter((row) => {
			if (row.event.type !== "custom") return false;
			const severity = row.event.severity ?? "info";
			return levels.has(severity) && (!query || row.event.name.toLowerCase().includes(query) || JSON.stringify(row.event.props ?? {}).toLowerCase().includes(query));
		});
		const bar = h("div", { class: "backstory-player-toolbar" });
		const search = h("input", {
			type: "search",
			placeholder: "Filter name or properties",
			value: text,
			"aria-label": "Filter custom events"
		});
		search.addEventListener("input", () => {
			text = search.value;
			draw();
		});
		bar.appendChild(search);
		for (const level of CUSTOM_SEVERITIES) bar.appendChild(h("button", {
			class: `backstory-player-chip ${level === "error" ? "danger" : ""}`,
			"aria-pressed": levels.has(level),
			text: `${level}${counts[level] ? ` ${counts[level]}` : ""}`,
			onclick: () => {
				if (levels.has(level)) levels.delete(level);
				else levels.add(level);
				draw();
			}
		}));
		root.appendChild(bar);
		const body = h("div", {
			class: "backstory-player-body",
			role: "log",
			"aria-label": "Custom events"
		});
		if (!rows.length) body.appendChild(h("div", {
			class: "backstory-player-empty",
			text: all.length ? "No custom events match this filter." : ctx.input.loading ? "Loading custom events…" : ctx.input.live ? "No custom events since this live session began." : "No custom events in this session. Record one with Backstory.track(name, properties, { severity })."
		}));
		for (const row of rows) {
			if (row.event.type !== "custom") continue;
			const severity = row.event.severity ?? "info";
			const detail = h("div", {}, h("div", {}, h("span", {
				class: `backstory-player-level ${severity}`,
				text: severity
			}), " ", h("strong", { text: row.event.name })));
			if (row.event.props && Object.keys(row.event.props).length) {
				const props = h("details", { class: "backstory-player-obj" });
				props.appendChild(h("summary", {
					class: "backstory-player-muted",
					text: summarizeValue(row.event.props)
				}));
				props.appendChild(h("pre", {
					class: "backstory-player-pre",
					text: JSON.stringify(row.event.props, null, 2)
				}));
				detail.appendChild(props);
			}
			body.appendChild(h("div", {
				class: `backstory-player-row ${row.t > ctx.input.now ? "future" : ""}`,
				"aria-current": row.t <= ctx.input.now && !rows.some((next) => next.t > row.t && next.t <= ctx.input.now) ? "true" : void 0,
				onclick: (e) => {
					if (!e.target.closest("details")) ctx?.actions.seek(row.t);
				}
			}, timeCell(ctx, row.t), detail));
		}
		root.appendChild(body);
	};
	return {
		update(next) {
			ctx = next;
			draw();
		},
		destroy() {
			root.remove();
		}
	};
}
function assistCapability(capability) {
	switch (capability) {
		case "cobrowse": return "Support access";
		case "voice": return "Voice";
		case "annotate": return "Annotations";
		case "control": return "Remote control";
		case "record": return "Recording";
		default: return "Access";
	}
}
function assistDescription(event) {
	const payload = event.payload ?? {};
	const capability = assistCapability(event.capability);
	switch (event.kind) {
		case "consent_request": return `${capability} requested`;
		case "consent_response": return event.granted ? `${capability} granted` : `${capability} ended`;
		case "annotation": {
			const kind = String(payload.kind ?? "annotation");
			return kind === "clear" ? "Annotations cleared" : `Drew ${kind}`;
		}
		case "control_start": return "Remote control started";
		case "control_stop": return "Remote control stopped";
		case "action":
			if (payload.chat) return `Chat message sent (${payload.length ?? 0} characters)`;
			if (payload.kind === "call_started") return "Voice call started";
			if (payload.kind === "call_ended") return "Voice call ended";
			if (payload.kind === "minutes_ran_out") return "Live Assist ended because the included minutes ran out";
			if (payload.kind === "unavailable") return "Live Assist ended because it is no longer included for this account";
			if (event.capability === "control") {
				const result = payload.ok === false ? ` failed${payload.error ? `: ${payload.error}` : ""}` : "";
				return `Remote ${String(payload.kind ?? "action")}${result}`;
			}
			return `${capability}: ${String(payload.kind ?? "action")}`;
	}
}
function assistActor(actor) {
	if (actor === "user") return "User";
	if (actor === "system") return "System";
	return "Support";
}
function createAssistPanel(container) {
	let ctx = null;
	const root = h("div", { class: "backstory-player backstory-player-assist" });
	container.appendChild(root);
	const draw = () => {
		if (!ctx) return;
		clear(root);
		const assistRows = ctx.input.events.filter((row) => row.event.type === "assist");
		const lastAnnotation = /* @__PURE__ */ new Map();
		assistRows.forEach((row, i) => {
			if (row.event.kind === "annotation" && row.event.payload?.id) lastAnnotation.set(String(row.event.payload.id), i);
		});
		const rows = assistRows.filter((row, i) => {
			const id = row.event.kind === "annotation" ? row.event.payload?.id : void 0;
			return !id || lastAnnotation.get(String(id)) === i;
		});
		const body = h("div", {
			class: "backstory-player-body",
			role: "log",
			"aria-label": "Assist activity"
		});
		if (!rows.length) body.appendChild(h("div", {
			class: "backstory-player-empty",
			text: ctx.input.loading ? "Loading assist activity…" : "No support activity in this session."
		}));
		for (const row of rows) {
			const event = row.event;
			const actor = assistActor(event.actor);
			body.appendChild(h("div", {
				class: `backstory-player-row ${row.t > ctx.input.now ? "future" : ""}`,
				"aria-current": row.t <= ctx.input.now && !rows.some((next) => next.t > row.t && next.t <= ctx.input.now) ? "true" : void 0,
				onclick: () => ctx?.actions.seek(row.t)
			}, timeCell(ctx, row.t), h("div", {}, h("div", { text: assistDescription(event) }), h("div", {
				class: "backstory-player-muted",
				text: actor
			}))));
		}
		root.appendChild(body);
	};
	return {
		update(next) {
			ctx = next;
			draw();
		},
		destroy() {
			root.remove();
		}
	};
}
function createNetworkPanel(container) {
	const kinds = /* @__PURE__ */ new Set([
		"fetch",
		"xhr",
		"resource",
		"ws",
		"beacon",
		"sse"
	]);
	let failedOnly = false;
	let slowOnly = false;
	let text = "";
	let tab = "requests";
	let selected = null;
	let traces = null;
	let ctx = null;
	const root = h("div", { class: "backstory-player backstory-player-network" });
	container.appendChild(root);
	const draw = () => {
		if (!ctx) return;
		clear(root);
		const all = deriveNetwork(ctx.input);
		const rows = deriveNetwork(ctx.input, {
			kinds: tab === "ws" ? /* @__PURE__ */ new Set(["ws"]) : kinds,
			failedOnly,
			slowOnly: tab === "ws" ? false : slowOnly,
			text
		});
		const sum = summarizeNetwork(all);
		root.appendChild(h("div", {
			class: "backstory-player-tabs",
			role: "tablist"
		}, h("button", {
			class: "backstory-player-tab",
			role: "tab",
			"aria-selected": tab === "requests",
			text: "Requests",
			onclick: () => {
				tab = "requests";
				draw();
			}
		}), h("button", {
			class: "backstory-player-tab",
			role: "tab",
			"aria-selected": tab === "ws",
			text: `WebSocket${sum.byKind.ws ? ` ${sum.byKind.ws}` : ""}`,
			onclick: () => {
				tab = "ws";
				draw();
			}
		})));
		const wsRows = all.filter((r) => r.kind === "ws");
		const wsOpen = wsRows.filter((r) => r.wsState === "open" || r.wsState === "connecting").length;
		const wsDropped = wsRows.filter((r) => r.wsState === "dropped" || r.wsState === "error").length;
		const bar = h("div", { class: "backstory-player-toolbar" });
		const search = h("input", {
			type: "search",
			placeholder: tab === "ws" ? "Filter URL or close code" : "Filter URL, operation, status",
			value: text,
			"aria-label": tab === "ws" ? "Filter sockets" : "Filter requests"
		});
		search.addEventListener("input", () => {
			text = search.value;
			draw();
		});
		bar.appendChild(search);
		if (tab === "ws") bar.appendChild(h("button", {
			class: "backstory-player-chip danger",
			"aria-pressed": failedOnly,
			text: `Dropped ${wsDropped}`,
			onclick: () => {
				failedOnly = !failedOnly;
				draw();
			}
		}));
		else {
			bar.appendChild(h("button", {
				class: "backstory-player-chip danger",
				"aria-pressed": failedOnly,
				text: `Failed ${sum.failed}`,
				onclick: () => {
					failedOnly = !failedOnly;
					draw();
				}
			}));
			bar.appendChild(h("button", {
				class: "backstory-player-chip",
				"aria-pressed": slowOnly,
				text: `Slow ${sum.slow}`,
				onclick: () => {
					slowOnly = !slowOnly;
					draw();
				}
			}));
			for (const k of [
				"fetch",
				"xhr",
				"resource"
			]) bar.appendChild(h("button", {
				class: "backstory-player-chip",
				"aria-pressed": kinds.has(k),
				text: `${k}${sum.byKind[k] ? ` ${sum.byKind[k]}` : ""}`,
				onclick: () => {
					if (kinds.has(k)) kinds.delete(k);
					else kinds.add(k);
					draw();
				}
			}));
		}
		root.appendChild(bar);
		root.appendChild(tab === "ws" ? h("div", { class: "backstory-player-summary" }, h("span", { text: `${wsRows.length} socket${wsRows.length === 1 ? "" : "s"}` }), h("span", { text: `${wsOpen} open` }), h("span", { text: `${wsDropped} dropped` }), h("span", { text: fmtBytes(wsRows.reduce((n, r) => n + r.wsBytes, 0)) })) : h("div", { class: "backstory-player-summary" }, h("span", { text: `${sum.requests} requests` }), h("span", { text: fmtBytes(sum.bytes) }), h("span", { text: `${sum.failed} failed` })));
		const body = h("div", { class: "backstory-player-body" });
		if (tab === "ws") body.appendChild(h("div", { class: "backstory-player-net ws head" }, h("span", { text: "Path" }), h("span", { text: "State" }), h("span", { text: "Handshake" }), h("span", { text: "Lifetime" }), h("span", { text: "Traffic" }), h("span", { text: "Conversation" })));
		else body.appendChild(h("div", { class: "backstory-player-net head" }, h("span", { text: "Method" }), h("span", { text: "Path" }), h("span", { text: "Status" }), h("span", { text: "Size" }), h("span", { text: "Time" }), h("span", { text: "Waterfall" })));
		if (!rows.length) {
			const live = ctx.input.live;
			const loading = ctx.input.loading;
			const empty = tab === "ws" ? live ? "No WebSocket connections in this session yet." : loading ? "Loading the rest of the session…" : "No WebSocket connections recorded." : live ? "No requests in this session yet." : loading ? "Loading the rest of the session…" : "No requests match.";
			body.appendChild(h("div", {
				class: "backstory-player-empty",
				text: empty
			}));
		}
		const span = Math.max(1, ctx.input.endTs - ctx.input.startTs);
		for (const r of rows) {
			if (tab === "ws") {
				const stateClass = r.wsState === "dropped" || r.wsState === "error" ? "bad" : r.wsState === "open" ? "live" : "ok";
				const ticks = wsSparkTicks(r);
				const spark = h("div", {
					class: "backstory-player-ws-spark",
					title: `${r.wsSent} sent · ${r.wsReceived} received`
				}, ...ticks.map((tick) => h("i", {
					class: tick.direction,
					style: `left:${tick.pct}%`
				})));
				body.appendChild(h("div", {
					class: `backstory-player-net ws ${r.future ? "future" : ""}`,
					"aria-current": r.current ? "true" : void 0,
					"data-key": r.key,
					onclick: () => {
						selected = selected === r.key ? null : r.key;
						draw();
					}
				}, h("span", {
					class: "path",
					title: r.url
				}, h("span", { text: r.path }), r.host ? h("small", { text: ` ${r.host}` }) : null), h("span", {
					class: `backstory-player-status ${stateClass}`,
					text: wsStateLabel(r)
				}), h("span", {
					class: "backstory-player-muted",
					text: fmtMs(r.wsHandshakeMs)
				}), h("span", {
					class: "backstory-player-muted",
					text: fmtMs(r.wsLifetimeMs)
				}), h("span", {
					class: "backstory-player-muted",
					text: `↑${r.wsSent} ↓${r.wsReceived}`
				}), spark));
				continue;
			}
			const statusClass = r.failed ? "bad" : (r.status ?? 0) >= 300 ? "warn" : "ok";
			const wf = h("div", {
				class: "backstory-player-wf",
				title: `${fmtClock(rel(ctx.input, r.t))} · ${fmtMs(r.durationMs)}`
			}, h("i", {
				class: `${r.failed ? "failed" : ""} ${r.kind}`,
				style: `left:${(r.t - ctx.input.startTs) / span * 100}%;width:${Math.max(.3, (r.durationMs ?? 0) / span * 100)}%`
			}));
			body.appendChild(h("div", {
				class: `backstory-player-net ${r.future ? "future" : ""}`,
				"aria-current": r.current ? "true" : void 0,
				"data-key": r.key,
				onclick: () => {
					selected = selected === r.key ? null : r.key;
					draw();
				}
			}, h("span", {
				class: "backstory-player-mono",
				text: r.kind === "resource" ? r.initiatorType ?? "res" : r.method
			}), h("span", {
				class: "path",
				title: r.url
			}, h("span", { text: r.operation ? `${r.operation} ` : "" }), h("span", { text: r.path }), r.host ? h("small", { text: ` ${r.host}` }) : null), h("span", {
				class: `backstory-player-status ${statusClass}`,
				text: r.failed && !r.status ? "ERR" : String(r.status ?? "…")
			}), h("span", {
				class: "backstory-player-muted",
				text: fmtBytes(r.size)
			}), h("span", {
				class: "backstory-player-muted",
				text: fmtMs(r.durationMs)
			}), wf));
		}
		root.appendChild(body);
		const sel = rows.find((r) => r.key === selected) ?? all.find((r) => r.key === selected);
		if (sel) root.appendChild(renderDrawer(sel));
	};
	const renderDrawer = (r) => {
		const c = ctx;
		const d = h("div", {
			class: "backstory-player-drawer",
			role: "region",
			"aria-label": "Request details"
		});
		d.appendChild(h("div", { style: "display:flex;gap:6px;align-items:center" }, h("strong", {
			class: "backstory-player-mono",
			text: r.kind === "ws" ? r.path : `${r.method} ${r.path}`
		}), h("span", { style: "flex:1" }), h("button", {
			class: "backstory-player-btn",
			text: "Jump to",
			onclick: () => c.actions.seek(r.t)
		}), r.kind === "ws" ? null : h("button", {
			class: "backstory-player-btn",
			text: "Copy as cURL",
			onclick: () => c.actions.copy?.(toCurl(r))
		}), h("button", {
			class: "backstory-player-btn",
			text: "Close",
			onclick: () => {
				selected = null;
				draw();
			}
		})));
		const kv = h("dl", { class: "backstory-player-kv" });
		const add = (k, v) => {
			if (v != null && v !== "") {
				kv.appendChild(h("dt", { text: k }));
				kv.appendChild(h("dd", { text: v }));
			}
		};
		add("URL", r.url);
		if (r.kind === "ws") {
			add("State", wsStateLabel(r));
			add("Close code", r.wsCloseCode != null ? String(r.wsCloseCode) : void 0);
			add("Handshake", fmtMs(r.wsHandshakeMs));
			add("Lifetime", fmtMs(r.wsLifetimeMs));
			add("Sent", `${r.wsSent} frames`);
			add("Received", `${r.wsReceived} frames`);
			add("Frame bytes", fmtBytes(r.wsBytes));
			const cadence = wsCadenceMs(r);
			add("Frame cadence", cadence != null ? fmtMs(cadence) : void 0);
		} else {
			add("Status", r.status != null ? String(r.status) : r.failed ? "network error" : void 0);
			add("Kind", r.kind);
			add("Initiator", r.initiatorType);
			add("Size", fmtBytes(r.size));
			add("Duration", fmtMs(r.durationMs));
			add("Operation", r.operation);
		}
		add("Started", fmtClock(rel(c.input, r.t)));
		add("Trace id", r.traceId);
		d.appendChild(h("h4", { text: "General" }));
		d.appendChild(kv);
		const segs = r.kind === "ws" ? [] : timingSegments(r);
		if (segs.length) {
			d.appendChild(h("h4", { text: "Timing" }));
			const total = segs.reduce((a, s) => a + s.ms, 0) || 1;
			d.appendChild(h("div", { class: "backstory-player-timing" }, ...segs.map((s) => h("i", {
				class: s.name,
				style: `width:${s.ms / total * 100}%`,
				title: `${s.name} ${fmtMs(s.ms)}`
			}))));
			d.appendChild(h("div", { class: "backstory-player-legend" }, ...segs.map((s) => h("span", {}, h("i", {
				class: s.name,
				style: `background:inherit`
			}), `${s.name} ${fmtMs(s.ms)}`))));
		}
		const headers = (title, hs) => {
			if (!hs || !Object.keys(hs).length) return;
			d.appendChild(h("h4", { text: title }));
			const l = h("dl", { class: "backstory-player-kv" });
			for (const [k, v] of Object.entries(hs)) {
				l.appendChild(h("dt", { text: k }));
				l.appendChild(h("dd", { text: v }));
			}
			d.appendChild(l);
		};
		headers("Request headers", r.reqHeaders);
		headers("Response headers", r.resHeaders);
		const kb = (n) => `${Math.max(1, Math.round((n ?? 0) / 1024))} KB`;
		const body = (title, b, note) => {
			if (!b && !note) return;
			d.appendChild(h("h4", { text: title }));
			if (b) {
				let pretty = b;
				try {
					pretty = JSON.stringify(JSON.parse(b), null, 2);
				} catch {}
				d.appendChild(h("pre", {
					class: "backstory-player-pre",
					text: pretty
				}));
			}
			if (note) d.appendChild(h("p", {
				class: "backstory-player-muted",
				text: note
			}));
		};
		body("Request body", r.reqBody, r.reqBodyCut ? `Cut at ${kb(r.reqBodyBytes)}` : void 0);
		body("Response body", r.resBody, r.resBodySkipped ? `Response not kept, it was ${kb(r.size)}` : void 0);
		if (r.kind === "ws") {
			d.appendChild(h("h4", { text: `Messages (${r.wsFrames.length})` }));
			if (!r.wsFrames.length) d.appendChild(h("p", {
				class: "backstory-player-muted",
				text: "No WebSocket messages were recorded for this connection."
			}));
			else for (const frame of r.wsFrames) {
				const label = `${frame.direction === "send" ? "↑ Sent" : "↓ Received"} · ${frame.dataType} · ${fmtBytes(frame.bytes)} · ${fmtClock(rel(c.input, frame.t))}`;
				d.appendChild(h("div", { class: "backstory-player-ws-frame" }, h("div", {
					class: "backstory-player-muted",
					text: label
				}), frame.text ? h("pre", {
					class: "backstory-player-pre",
					text: prettyBody(frame.text)
				}) : h("div", {
					class: "backstory-player-muted",
					text: frame.dataType === "binary" ? "Binary payload not captured." : "Text payload capture is off or this URL is not allowlisted."
				})));
			}
		} else if (!r.reqHeaders && !r.reqBody && !r.resBody && !r.resBodySkipped) d.appendChild(h("p", {
			class: "backstory-player-muted",
			text: "Headers and bodies were not recorded for this call."
		}));
		d.appendChild(h("h4", { text: "Backend trace" }));
		if (traces === null) {
			d.appendChild(h("p", {
				class: "backstory-player-muted",
				text: c.fetchers?.traces ? "Loading traces…" : "Connect OpenTelemetry to see backend spans under this request."
			}));
			loadTraces();
		} else {
			const tr = traceForRow(r, traces);
			if (!tr) d.appendChild(h("p", {
				class: "backstory-player-muted",
				text: "No linked trace for this request."
			}));
			else {
				const nodes = spanTree(tr);
				const total = Math.max(1, tr.durationMs);
				for (const { span, depth } of nodes) d.appendChild(h("div", { class: `backstory-player-span ${span.status === "error" ? "error" : ""}` }, h("span", {
					class: "backstory-player-mono",
					style: `padding-left:${depth * 12}px`,
					text: `${span.service} · ${span.name}`
				}), h("span", { class: "bar" }, h("i", { style: `left:${(span.startMs - tr.startMs) / total * 100}%;width:${span.durationMs / total * 100}%` })), h("span", {
					class: "backstory-player-muted",
					text: fmtMs(span.durationMs)
				})));
			}
		}
		return d;
	};
	const loadTraces = async () => {
		if (traces !== null || !ctx?.fetchers?.traces) return;
		try {
			traces = await ctx.fetchers.traces();
		} catch {
			traces = [];
		}
		draw();
	};
	return {
		update(next) {
			ctx = next;
			draw();
		},
		destroy() {
			root.remove();
		}
	};
}
function prettyBody(value) {
	try {
		return JSON.stringify(JSON.parse(value), null, 2);
	} catch {
		return value;
	}
}
function cpuPressureLabel(state) {
	switch (state) {
		case "nominal": return "steady";
		case "fair": return "busy";
		case "serious": return "under pressure";
		case "critical": return "overloaded";
	}
}
function renderMemorySeries(input, samples, seek) {
	const wrap = h("div", { class: "backstory-player-memory" });
	const atPlayhead = (has) => [...samples].reverse().find((s) => s.t <= input.now && has(s)) ?? samples.find(has);
	const latestHeap = atPlayhead((s) => s.jsHeapUsedMB != null);
	const latestRecorder = atPlayhead((s) => s.recorderPressure != null || s.recorderUsedMB != null);
	const latestCpu = atPlayhead((s) => s.cpuState != null);
	const stats = h("div", { class: "backstory-player-memory-stats" });
	stats.appendChild(h("span", { class: "heap" }, h("i"), h("span", {}, "Page JS heap", h("strong", { text: latestHeap ? `${latestHeap.jsHeapUsedMB.toFixed(1)} MB${latestHeap.jsHeapLimitMB ? ` / ${latestHeap.jsHeapLimitMB.toFixed(0)} MB` : ""}` : "not exposed by this browser" }))));
	stats.appendChild(h("span", { class: "recorder" }, h("i"), h("span", {}, "Recorder buffer", h("strong", { text: latestRecorder?.recorderPressure != null ? `${Math.round(latestRecorder.recorderPressure * 100)}%${latestRecorder.recorderUsedMB != null ? ` · ${latestRecorder.recorderUsedMB.toFixed(1)} MB` : ""}` : "waiting for a sample" }))));
	stats.appendChild(h("span", { class: "cpu" }, h("i"), h("span", {}, "CPU pressure", h("strong", { text: latestCpu?.cpuState ? cpuPressureLabel(latestCpu.cpuState) : "not reported" }))));
	wrap.appendChild(stats);
	const ns = "http://www.w3.org/2000/svg";
	const svg = document.createElementNS(ns, "svg");
	svg.setAttribute("class", "backstory-player-memory-chart");
	svg.setAttribute("viewBox", "0 0 100 40");
	svg.setAttribute("preserveAspectRatio", "none");
	svg.setAttribute("role", "img");
	svg.setAttribute("aria-label", "Page JavaScript heap and Backstory recorder buffer pressure over the session");
	const span = Math.max(1, input.endTs - input.startTs);
	const points = (kind) => samples.flatMap((sample) => {
		const value = kind === "heap" ? sample.jsHeapUsedMB != null && sample.jsHeapLimitMB ? sample.jsHeapUsedMB / sample.jsHeapLimitMB : void 0 : sample.recorderPressure ?? (sample.recorderUsedMB != null && sample.recorderBudgetMB ? sample.recorderUsedMB / sample.recorderBudgetMB : void 0);
		if (value == null || !Number.isFinite(value)) return [];
		const x = Math.max(0, Math.min(100, (sample.t - input.startTs) / span * 100));
		const y = 38 - Math.max(0, Math.min(1, value)) * 36;
		const title = kind === "heap" ? `Page JS heap ${sample.jsHeapUsedMB.toFixed(1)} MB${sample.jsHeapLimitMB ? ` (${Math.round(value * 100)}% of limit)` : ""}` : `Recorder buffer ${Math.round(value * 100)}%${sample.recorderUsedMB != null ? ` (${sample.recorderUsedMB.toFixed(1)} MB)` : ""}`;
		return [{
			x,
			y,
			t: sample.t,
			title
		}];
	});
	for (const kind of ["heap", "recorder"]) {
		const series = points(kind);
		if (!series.length) continue;
		const line = document.createElementNS(ns, "polyline");
		line.setAttribute("class", kind);
		line.setAttribute("points", series.map((p) => `${p.x},${p.y}`).join(" "));
		svg.appendChild(line);
		for (const point of series) {
			const dot = document.createElementNS(ns, "circle");
			dot.setAttribute("class", kind);
			dot.setAttribute("cx", String(point.x));
			dot.setAttribute("cy", String(point.y));
			dot.setAttribute("r", "1.2");
			dot.addEventListener("click", () => seek(point.t));
			const title = document.createElementNS(ns, "title");
			title.textContent = `${point.title} at ${fmtClock(rel(input, point.t))}`;
			dot.appendChild(title);
			svg.appendChild(dot);
		}
	}
	for (const sample of samples) {
		const state = sample.cpuState === "serious" || sample.cpuState === "critical" ? sample.cpuState : sample.pressureState;
		if (state !== "serious" && state !== "critical") continue;
		const marker = document.createElementNS(ns, "line");
		const x = Math.max(0, Math.min(100, (sample.t - input.startTs) / span * 100));
		marker.setAttribute("class", `pressure ${state}`);
		marker.setAttribute("x1", String(x));
		marker.setAttribute("x2", String(x));
		marker.setAttribute("y1", "1");
		marker.setAttribute("y2", "39");
		svg.appendChild(marker);
	}
	const playhead = document.createElementNS(ns, "line");
	const phX = Math.max(0, Math.min(100, (input.now - input.startTs) / span * 100));
	playhead.setAttribute("class", "playhead");
	playhead.setAttribute("x1", String(phX));
	playhead.setAttribute("x2", String(phX));
	playhead.setAttribute("y1", "0");
	playhead.setAttribute("y2", "40");
	svg.appendChild(playhead);
	wrap.appendChild(svg);
	if (input.loading) wrap.appendChild(h("p", {
		class: "backstory-player-muted",
		text: "Loading the rest of the recording…"
	}));
	wrap.appendChild(h("p", {
		class: "backstory-player-muted",
		text: "JS heap is Chromium-only and excludes DOM, images, GPU and other processes. CPU pressure is the browser’s own level, not a percentage. Recorder buffer is Backstory’s bounded upload queue."
	}));
	return wrap;
}
function createPerformancePanel(container) {
	let ctx = null;
	const root = h("div", { class: "backstory-player backstory-player-perf" });
	container.appendChild(root);
	const draw = () => {
		if (!ctx) return;
		clear(root);
		const m = derivePerformance(ctx.input);
		const body = h("div", { class: "backstory-player-body" });
		body.appendChild(h("div", { class: "backstory-player-h" }, h("span", { text: "Web Vitals" }), h("span", {
			class: "backstory-player-muted",
			text: "click a card to highlight its element"
		})));
		if (!m.vitals.length) body.appendChild(h("div", {
			class: "backstory-player-empty",
			text: "No Web Vitals reported for this segment."
		}));
		const cards = h("div", { class: "backstory-player-cards" });
		for (const v of m.vitals) cards.appendChild(h("button", {
			class: `backstory-player-card ${v.rating} ${v.future ? "future" : ""}`,
			title: v.future ? "Reported later in the session" : `Reported at ${fmtClock(rel(ctx.input, v.t))}`,
			onclick: () => {
				if (v.name === "CLS" && v.sources?.length) ctx.actions.drawBoxes?.(v.sources.map((s) => s.curr), {
					ms: 2e3,
					label: "layout shift"
				});
				else if (v.nodeId != null) ctx.actions.highlightNode?.(v.nodeId, {
					ms: 2e3,
					label: v.name
				});
				ctx.actions.seek(v.t);
			}
		}, h("div", {
			class: "name",
			text: v.name
		}), h("div", {
			class: "val",
			text: formatVital(v)
		}), h("div", {
			class: "backstory-player-muted",
			text: v.rating.replace("-", " ")
		})));
		body.appendChild(cards);
		const span = Math.max(1, ctx.input.endTs - ctx.input.startTs);
		const phLeft = `${(ctx.input.now - ctx.input.startTs) / span * 100}%`;
		body.appendChild(h("div", { class: "backstory-player-h" }, h("span", { text: `Long tasks (${m.totals.longTasks}, ${fmtMs(m.totals.longTaskMs)} total${m.totals.selfLongTasks ? `, ${m.totals.selfLongTasks} attributed to the recorder` : ""})` })));
		const lane = h("div", {
			class: "backstory-player-lane",
			role: "img",
			"aria-label": "Long tasks lane"
		});
		for (const lt of m.longTasks) lane.appendChild(h("i", {
			class: lt.self ? "self" : "",
			style: `left:${(lt.t - ctx.input.startTs) / span * 100}%;width:${Math.max(.2, lt.durationMs / span * 100)}%`,
			title: `${fmtMs(lt.durationMs)} at ${fmtClock(rel(ctx.input, lt.t))}${lt.self ? " (recorder)" : ""}`,
			onclick: () => ctx.actions.seek(lt.t)
		}));
		lane.appendChild(h("span", {
			class: "ph",
			style: `left:${phLeft}`
		}));
		body.appendChild(lane);
		body.appendChild(h("div", { class: "backstory-player-h" }, h("span", { text: "Activity (approximation from event density)" })));
		const act = h("div", {
			class: "backstory-player-lane",
			role: "img",
			"aria-label": "Activity strip"
		});
		const step = 100 / Math.max(1, m.activity.length);
		m.activity.forEach((b, i) => {
			if (b.busy > .02) act.appendChild(h("i", {
				class: "act",
				style: `left:${i * step}%;width:${step}%;opacity:${.15 + b.busy * .85}`,
				title: `${b.events} events, ${b.mutations} mutations`
			}));
		});
		act.appendChild(h("span", {
			class: "ph",
			style: `left:${phLeft}`
		}));
		body.appendChild(act);
		body.appendChild(h("div", { class: "backstory-player-h" }, h("span", { text: "Memory pressure" }), h("span", {
			class: "backstory-player-muted",
			text: "click a sample to seek"
		})));
		if (m.memory.length) body.appendChild(renderMemorySeries(ctx.input, m.memory, (t) => ctx.actions.seek(t)));
		else body.appendChild(h("div", {
			class: "backstory-player-empty",
			text: "No memory samples in this recording."
		}));
		root.appendChild(body);
	};
	return {
		update(next) {
			ctx = next;
			draw();
		},
		destroy() {
			root.remove();
		}
	};
}
function createStatePanel(container) {
	let ctx = null;
	let store;
	let selected = null;
	const root = h("div", { class: "backstory-player backstory-player-state" });
	container.appendChild(root);
	const draw = () => {
		if (!ctx) return;
		clear(root);
		const m = deriveState(ctx.input, store);
		const bar = h("div", { class: "backstory-player-toolbar" });
		bar.appendChild(h("button", {
			class: "backstory-player-chip",
			"aria-pressed": !store,
			text: "All stores",
			onclick: () => {
				store = void 0;
				draw();
			}
		}));
		for (const s of m.stores) bar.appendChild(h("button", {
			class: "backstory-player-chip",
			"aria-pressed": store === s,
			text: s,
			onclick: () => {
				store = s;
				draw();
			}
		}));
		root.appendChild(bar);
		const split = h("div", { class: "backstory-player-split" });
		const list = h("div", {
			class: "backstory-player-body",
			role: "list"
		});
		if (!m.entries.length) {
			const text = ctx.input.loading ? "Loading state history…" : ctx.input.live ? "No state changes in the loaded live history. State capture is opt-in; attach a Redux, Zustand, Pinia, NgRx, MobX, TanStack Query, Apollo, React, or custom bridge in the SDK." : "No application state was captured in this recording. State capture is opt-in; attach a Redux, Zustand, Pinia, NgRx, MobX, TanStack Query, Apollo, React, or custom bridge in the SDK.";
			list.appendChild(h("div", {
				class: "backstory-player-empty",
				text
			}));
		}
		for (const en of m.entries) list.appendChild(h("div", {
			class: `backstory-player-row ${en.future ? "future" : ""}`,
			role: "listitem",
			"aria-current": en.current ? "true" : void 0,
			"aria-selected": selected === en.key,
			"data-key": en.key,
			onclick: () => {
				selected = en.key;
				ctx.actions.seek(en.t);
				draw();
			}
		}, timeCell(ctx, en.t), h("div", {}, h("span", {
			class: "backstory-player-mono",
			text: en.action ?? (en.hasSnapshot ? "(snapshot)" : "(update)")
		}), h("span", {
			class: "backstory-player-muted",
			text: ` ${en.storeName} ${summarizeDiff(en.diff)}${en.truncated ? ` · ${fmtBytes(en.truncated)} truncated` : ""}`
		}))));
		split.appendChild(list);
		const detail = h("div", { class: "backstory-player-drawer" });
		const sel = m.entries.find((e) => e.key === selected);
		if (!sel) detail.appendChild(h("div", {
			class: "backstory-player-empty",
			text: "Select an action to see its diff and the reconstructed state."
		}));
		else {
			if (sel.diff?.length) {
				detail.appendChild(h("h4", { text: `Diff (${sel.diff.length} ops)` }));
				const ul = h("ul", { class: "backstory-player-diff" });
				for (const d of sel.diff) ul.appendChild(h("li", {
					class: d.op,
					text: `${d.op === "add" ? "+" : d.op === "remove" ? "−" : "~"} ${d.path}${d.op !== "remove" ? ` = ${JSON.stringify(d.value)?.slice(0, 200) ?? ""}` : ""}`
				}));
				detail.appendChild(ul);
			}
			const snap = reconstructState(ctx.input, sel.storeName, sel.key);
			detail.appendChild(h("h4", { text: "State after action" }));
			if (sel.store === "react" && isReactTree(snap)) detail.appendChild(renderReactTree(snap));
			else if (snap === void 0) detail.appendChild(h("p", {
				class: "backstory-player-muted",
				text: "No snapshot available to reconstruct from."
			}));
			else detail.appendChild(h("pre", {
				class: "backstory-player-pre",
				text: JSON.stringify(snap, null, 2)?.slice(0, 2e4) ?? String(snap)
			}));
		}
		split.appendChild(detail);
		root.appendChild(split);
	};
	return {
		update(next) {
			ctx = next;
			draw();
		},
		destroy() {
			root.remove();
		}
	};
}
function renderReactTree(node, depth = 0) {
	const props = node.props ? Object.keys(node.props) : [];
	const label = h("span", {}, h("span", {
		class: "tag",
		text: `<${node.name}>`
	}), props.length ? h("span", {
		class: "props",
		text: ` ${props.slice(0, 6).join(" ")}${props.length > 6 ? " …" : ""}`
	}) : null);
	if (!node.children?.length) return h("div", {
		class: depth === 0 ? "backstory-player-rtree" : "",
		style: `margin-left:${depth ? 10 : 0}px`
	}, label);
	const d = h("details", { open: depth < 2 });
	d.appendChild(h("summary", {}, label));
	for (const c of node.children) d.appendChild(renderReactTree(c, depth + 1));
	return depth === 0 ? h("div", { class: "backstory-player-rtree" }, d) : d;
}
function createApplicationPanel(container) {
	let ctx = null;
	let tab = null;
	const root = h("div", { class: "backstory-player backstory-player-app" });
	container.appendChild(root);
	const section = (title, ...kids) => h("div", {}, h("div", { class: "backstory-player-h" }, h("span", { text: title })), ...kids);
	const kvList = (rows) => {
		const dl = h("dl", {
			class: "backstory-player-kv",
			style: "padding:0 8px"
		});
		for (const [k, v] of rows) {
			dl.appendChild(h("dt", { text: k }));
			dl.appendChild(h("dd", { text: v }));
		}
		return dl;
	};
	const draw = () => {
		if (!ctx) return;
		clear(root);
		const m = deriveApplication(ctx.input, ctx.meta);
		const env = Object.entries(m.env);
		const counts = {
			overview: m.device.length,
			routes: m.routes.length,
			storage: m.storage.keys.length,
			cookies: m.cookies.keys.length,
			workers: m.serviceWorker.state ? 1 : 0,
			flags: m.flags.length,
			experiments: m.experiments.length,
			environment: env.length
		};
		const labels = [
			["overview", "Overview"],
			["routes", "Routes"],
			["storage", "Storage"],
			["cookies", "Cookies"],
			["workers", "Workers"],
			["flags", "Flags"]
		];
		if (counts.experiments) labels.push(["experiments", "Experiments"]);
		if (counts.environment) labels.push(["environment", "Environment"]);
		if (!tab || !labels.some(([id]) => id === tab)) tab = labels.find(([id]) => counts[id] > 0)?.[0] ?? "overview";
		const tabs = h("div", {
			class: "backstory-player-tabs",
			role: "tablist"
		});
		for (const [id, label] of labels) {
			const n = id === "overview" ? 0 : counts[id];
			tabs.appendChild(h("button", {
				class: "backstory-player-tab",
				role: "tab",
				"aria-selected": tab === id,
				text: `${label}${n ? ` ${n}` : ""}`,
				onclick: () => {
					tab = id;
					draw();
				}
			}));
		}
		root.appendChild(tabs);
		const body = h("div", { class: "backstory-player-body" });
		const empty = (text) => h("div", {
			class: "backstory-player-empty",
			text
		});
		if (tab === "overview") {
			const rows = m.device.slice();
			if (ctx.location) {
				const at = rows.findIndex(([label]) => label === "Device");
				rows.splice(at >= 0 ? at + 1 : rows.length, 0, ["Location", ctx.location]);
			}
			body.appendChild(rows.length ? kvList(rows) : empty("Session metadata not loaded yet."));
		} else if (tab === "routes") {
			if (!m.routes.length) body.appendChild(empty("No navigations recorded in this segment."));
			for (const r of m.routes) body.appendChild(h("div", {
				class: `backstory-player-row ${r.future ? "future" : ""}`,
				"aria-current": r.current ? "true" : void 0,
				onclick: () => ctx.actions.seek(r.t)
			}, timeCell(ctx, r.t), h("div", {}, h("span", {
				class: "backstory-player-mono",
				text: r.route ?? new URL(r.url, "http://x").pathname
			}), h("div", {
				class: "backstory-player-muted",
				text: `${r.kind} · ${r.title ?? r.url}`
			}))));
		} else if (tab === "storage") body.appendChild(m.storage.keys.length ? kvList(m.storage.keys.map((k) => [k, m.storage.values[k] ?? "•••• (value not captured)"])) : empty("No storage snapshot captured."));
		else if (tab === "cookies") body.appendChild(m.cookies.keys.length ? section("Names only — values are never captured", h("div", {
			class: "backstory-player-mono",
			style: "padding:0 8px",
			text: m.cookies.keys.join(", ")
		})) : empty("No cookies snapshot captured."));
		else if (tab === "workers") body.appendChild(m.serviceWorker.state ? kvList([["Service worker", m.serviceWorker.state], ["Reported at", fmtClock(rel(ctx.input, m.serviceWorker.t ?? ctx.input.startTs))]]) : empty("No service worker reported by the page."));
		else if (tab === "flags") body.appendChild(m.flags.length ? kvList(m.flags.map((f) => [f.key, `${String(f.value)}${f.provider ? ` · ${f.provider}` : ""}`])) : empty("No feature flags captured. Connect LaunchDarkly, Statsig, Split or Unleash in the SDK."));
		else if (tab === "experiments") body.appendChild(kvList(m.experiments.map((f) => [f.key, `${String(f.value)}${f.provider ? ` · ${f.provider}` : ""}`])));
		else body.appendChild(kvList(env));
		root.appendChild(body);
	};
	return {
		update(next) {
			ctx = next;
			draw();
		},
		destroy() {
			root.remove();
		}
	};
}
function createAccessibilityPanel(container) {
	let ctx = null;
	let audit = null;
	let auditedAt = -1;
	let tab = "audit";
	const root = h("div", { class: "backstory-player backstory-player-a11y" });
	container.appendChild(root);
	const draw = () => {
		if (!ctx) return;
		clear(root);
		root.appendChild(h("div", {
			class: "backstory-player-tabs",
			role: "tablist"
		}, h("button", {
			class: "backstory-player-tab",
			role: "tab",
			"aria-selected": tab === "audit",
			text: "Audit",
			onclick: () => {
				tab = "audit";
				draw();
			}
		}), h("button", {
			class: "backstory-player-tab",
			role: "tab",
			"aria-selected": tab === "focus",
			text: "Focus order",
			onclick: () => {
				tab = "focus";
				draw();
			}
		})));
		const body = h("div", { class: "backstory-player-body" });
		if (tab === "focus") {
			const steps = focusPath(ctx.input);
			if (!steps.length) body.appendChild(h("div", {
				class: "backstory-player-empty",
				text: ctx.input.loading ? "Loading focus events…" : "No focus events recorded (the user did not use the keyboard to move focus)."
			}));
			steps.forEach((s, i) => body.appendChild(h("div", {
				class: `backstory-player-row ${s.future ? "future" : ""}`,
				onclick: () => {
					ctx.actions.seek(s.t);
					ctx.actions.highlightNode?.(s.nodeId, {
						ms: 1500,
						label: `focus #${i + 1}`
					});
				}
			}, timeCell(ctx, s.t), h("div", { text: `${i + 1}. node #${s.nodeId}` }))));
			root.appendChild(body);
			return;
		}
		const bar = h("div", { class: "backstory-player-toolbar" });
		bar.appendChild(h("button", {
			class: "backstory-player-btn",
			text: "Run audit at playhead",
			onclick: () => {
				runAudit();
				draw();
			}
		}));
		if (audit) bar.appendChild(h("span", {
			class: "backstory-player-muted",
			text: `${audit.issues.length} findings · ${audit.nodesChecked} nodes · ${audit.rulesRun} rules · at ${fmtClock(rel(ctx.input, auditedAt))}`
		}));
		root.appendChild(bar);
		if (!audit) body.appendChild(h("div", {
			class: "backstory-player-empty",
			text: "Run the audit to check the reconstructed page at the current moment."
		}));
		else {
			for (const sev of [
				"critical",
				"serious",
				"moderate",
				"minor"
			]) {
				const items = audit.issues.filter((i) => i.severity === sev);
				if (!items.length) continue;
				body.appendChild(h("div", { class: "backstory-player-h" }, h("span", {}, h("span", {
					class: `backstory-player-sev ${sev}`,
					text: sev
				}), `${items.length}`)));
				for (const i of items) body.appendChild(h("div", {
					class: "backstory-player-issue",
					onclick: () => {
						if (i.nodeId != null) ctx.actions.highlightNode?.(i.nodeId, {
							ms: 2e3,
							label: i.rule
						});
					}
				}, h("div", { class: "title" }, h("strong", { text: i.message })), h("div", {
					class: "backstory-player-muted",
					text: i.help
				}), h("div", {
					class: "backstory-player-mono backstory-player-muted",
					text: i.selector
				})));
			}
			if (!audit.issues.length) body.appendChild(h("div", {
				class: "backstory-player-empty",
				text: "No accessibility findings at this moment."
			}));
		}
		root.appendChild(body);
	};
	const runAudit = () => {
		const doc = ctx?.mirror?.iframe.contentDocument;
		if (!doc || !ctx) return;
		audit = auditAccessibility(doc, (el) => ctx.mirror.getId(el));
		auditedAt = ctx.input.now;
	};
	return {
		update(next) {
			ctx = next;
			draw();
		},
		destroy() {
			root.remove();
		}
	};
}
function createIssuesPanel(container) {
	let ctx = null;
	let issues = null;
	let clusters = [];
	let loading = false;
	let error = null;
	let tab = "issues";
	const root = h("div", { class: "backstory-player backstory-player-issues" });
	container.appendChild(root);
	const load = async () => {
		if (issues || loading || !ctx?.fetchers?.issues) return;
		loading = true;
		try {
			issues = await ctx.fetchers.issues();
			const ids = [...new Set(issues.map((i) => i.clusterId))];
			if (ids.length && ctx.fetchers.clusters) clusters = await ctx.fetchers.clusters(ids).catch(() => []);
		} catch (e) {
			error = e.message;
			issues = [];
		} finally {
			loading = false;
			draw();
		}
	};
	const draw = () => {
		if (!ctx) return;
		clear(root);
		root.appendChild(h("div", {
			class: "backstory-player-tabs",
			role: "tablist"
		}, h("button", {
			class: "backstory-player-tab",
			role: "tab",
			"aria-selected": tab === "issues",
			text: "Signals",
			onclick: () => {
				tab = "issues";
				draw();
			}
		}), h("button", {
			class: "backstory-player-tab",
			role: "tab",
			"aria-selected": tab === "why",
			text: "Why",
			onclick: () => {
				tab = "why";
				draw();
			}
		})));
		const body = h("div", { class: "backstory-player-body" });
		const rows = deriveIssues(ctx.input, issues ?? [], clusters);
		if (tab === "why") {
			const withAi = rows.filter((r) => r.cluster?.aiExplanation);
			body.appendChild(h("div", { class: "backstory-player-ai" }, h("strong", { text: "Why" }), h("p", {
				class: "backstory-player-muted",
				text: withAi.length ? "Explanations generated for this session's issue clusters:" : "AI explanations arrive in Phase 3. Once enabled, this tab explains what the user tried, what broke, and why, with citations that jump the replay."
			})));
			for (const r of withAi) {
				const ai = r.cluster.aiExplanation;
				body.appendChild(h("div", { class: "backstory-player-issue backstory-player-ai-output" }, h("div", {
					class: "backstory-player-ai-mark",
					text: "✦ AI generated"
				}), h("div", { class: "title" }, h("span", {
					class: `backstory-player-sev ${r.issue.severity}`,
					text: r.issue.severity
				}), h("strong", { text: r.cluster.title })), h("p", { text: ai.text }), h("div", {
					class: "backstory-player-muted",
					text: `Generated ${new Date(ai.generatedAt).toLocaleString()}`
				}), ...ai.citations.map((c) => h("button", {
					class: "ev",
					text: c,
					onclick: () => {
						const ev = r.evidence.find((e) => `${e.seq}:${e.idx}` === c);
						if (ev?.t != null) ctx.actions.seek(ev.t);
					}
				}))));
			}
			root.appendChild(body);
			return;
		}
		if (issues === null) {
			body.appendChild(h("div", {
				class: "backstory-player-empty",
				text: ctx.fetchers?.issues ? "Loading Signals…" : "Signal detection runs in the worker; connect the api to list Signals for this session."
			}));
			load();
		} else if (error) body.appendChild(h("div", {
			class: "backstory-player-empty",
			text: `Could not load Signals: ${error}`
		}));
		else if (!rows.length) body.appendChild(h("div", {
			class: "backstory-player-empty",
			text: "No Signals detected in this session."
		}));
		else {
			const counts = issueCounts(rows);
			root.appendChild(h("div", { class: "backstory-player-summary" }, ...SEVERITY_ORDER.filter((s) => counts[s]).map((s) => h("span", {}, h("span", {
				class: `backstory-player-sev ${s}`,
				text: s
			}), String(counts[s])))));
			for (const r of rows) {
				const chips = r.evidence.slice(0, 8).map((e) => h("button", {
					class: "ev",
					title: `${e.type ?? "event"} ${e.seq}:${e.idx}`,
					text: `${e.type ?? "evidence"}${e.t != null ? ` @ ${fmtClock(rel(ctx.input, e.t))}` : ""}`,
					onclick: (ev) => {
						ev.stopPropagation();
						if (e.t != null) ctx.actions.seek(e.t);
					}
				}));
				body.appendChild(h("div", {
					class: `backstory-player-issue signal-row ${r.active ? "active" : ""} ${r.future ? "future" : ""}`,
					onclick: () => {
						ctx.actions.seek(r.t);
						if (r.issue.nodeId != null) ctx.actions.highlightNode?.(r.issue.nodeId, {
							ms: 2e3,
							label: humanKind(r.issue.kind)
						});
					}
				}, h("time", {
					class: "signal-time",
					text: `${fmtClock(rel(ctx.input, r.t))}${r.end > r.t ? `–${fmtClock(rel(ctx.input, r.end))}` : ""}`
				}), h("div", { class: "signal-copy" }, h("div", { class: "title" }, h("strong", { text: r.cluster?.title ?? humanKind(r.issue.kind) }), h("span", {
					class: `backstory-player-sev ${r.issue.severity}`,
					text: r.issue.severity
				})), r.issue.selector ? h("div", {
					class: "backstory-player-mono backstory-player-muted",
					text: r.issue.selector
				}) : null, h("div", {
					class: "backstory-player-muted",
					text: `${r.issue.route ?? ""}${r.issue.errorFingerprint ? ` · ${r.issue.errorFingerprint}` : ""} · confidence ${Math.round(r.issue.confidence * 100)}%`
				}), h("div", {}, ...chips, r.cluster ? h("button", {
					class: "ev",
					text: `cluster ×${r.cluster.count}`,
					onclick: (ev) => {
						ev.stopPropagation();
						ctx.actions.openCluster?.(r.cluster.id);
					}
				}) : null))));
			}
		}
		root.appendChild(body);
	};
	return {
		update(next) {
			ctx = next;
			draw();
			load();
		},
		destroy() {
			root.remove();
		}
	};
}
function createElementsPanel(container) {
	let ctx = null;
	let model = null;
	const expanded = /* @__PURE__ */ new Set();
	let selected = null;
	let hover = null;
	let inspect = false;
	const root = h("div", { class: "backstory-player backstory-player-elements" });
	container.appendChild(root);
	let offInspect = null;
	const renderNode = (n, into) => {
		const key = n.nodeId ?? "root";
		const isOpen = expanded.has(key);
		const row = h("div", {
			class: "backstory-player-node",
			role: "treeitem",
			"aria-expanded": n.hasChildren ? isOpen : void 0,
			"aria-selected": selected != null && selected === n.nodeId,
			style: `padding-left:${6 + n.depth * 12}px`,
			"data-node": n.nodeId ?? "",
			onclick: () => {
				if (n.nodeId != null) {
					selected = n.nodeId;
					hover = null;
					ctx?.actions.highlightNode?.(n.nodeId, { ms: 1500 });
				}
				draw();
			},
			onmouseenter: () => {
				if (n.nodeId != null && n.nodeId !== hover) {
					hover = n.nodeId;
					ctx?.actions.highlightNode?.(n.nodeId, { ms: 0 });
				}
			},
			onmouseleave: () => {
				if (hover === n.nodeId) {
					hover = null;
					ctx?.actions.highlightNode?.(null);
				}
			}
		});
		const tw = h("span", {
			class: "tw",
			text: n.hasChildren ? isOpen ? "▾" : "▸" : ""
		});
		tw.addEventListener("click", (e) => {
			e.stopPropagation();
			if (isOpen) expanded.delete(key);
			else expanded.add(key);
			draw();
		});
		row.appendChild(tw);
		if (n.kind === "text") row.appendChild(h("span", {
			class: n.masked ? "masked" : "txt",
			text: `"${n.text ?? ""}"`
		}));
		else if (n.kind === "element") {
			const [tag, ...rest] = n.label.split(/(?=[#.\[])/);
			row.appendChild(h("span", {
				class: "tag",
				text: `<${tag}`
			}));
			if (rest.length) row.appendChild(h("span", {
				class: "attr",
				text: rest.join("")
			}));
			row.appendChild(h("span", {
				class: "tag",
				text: ">"
			}));
			if (n.placeholder) row.appendChild(h("span", {
				class: "txt",
				text: ` (${n.placeholder} placeholder)`
			}));
			if (n.masked) row.appendChild(h("span", {
				class: "masked",
				text: " masked"
			}));
			if (!isOpen && n.childCount) row.appendChild(h("span", {
				class: "txt",
				text: ` …${n.childCount}`
			}));
		} else row.appendChild(h("span", {
			class: "txt",
			text: n.label
		}));
		into.appendChild(row);
		if (n.hasChildren && isOpen && model) for (const c of model.children(n.nodeId, n.depth + 1)) renderNode(c, into);
	};
	const draw = () => {
		if (!ctx || !model) return;
		clear(root);
		const bar = h("div", { class: "backstory-player-toolbar" });
		bar.appendChild(h("button", {
			class: "backstory-player-btn",
			"aria-pressed": inspect,
			text: inspect ? "⌖ Inspecting… (click the replay)" : "⌖ Inspect",
			onclick: () => {
				inspect = !inspect;
				setInspect(inspect);
				draw();
			}
		}));
		bar.appendChild(h("span", {
			class: "backstory-player-muted",
			text: `${model.size} nodes`
		}));
		if (selected != null) bar.appendChild(h("button", {
			class: "backstory-player-btn",
			text: "Find sessions clicking this",
			onclick: () => {
				const d = model.details(selected);
				if (d) ctx.actions.findSessionsFor?.(d.selector);
			}
		}));
		root.appendChild(bar);
		const split = h("div", { class: "backstory-player-split" });
		const tree = h("div", {
			class: "backstory-player-tree",
			role: "tree"
		});
		const r = model.root();
		if (!r) tree.appendChild(h("div", {
			class: "backstory-player-empty",
			text: "No snapshot rendered yet."
		}));
		else {
			if (!expanded.size) {
				expanded.add(r.nodeId ?? "root");
				for (const c of model.children(r.nodeId, 1)) if (c.label.startsWith("body")) expanded.add(c.nodeId ?? "root");
			}
			renderNode(r, tree);
		}
		split.appendChild(tree);
		const details = h("div", {
			class: "backstory-player-drawer",
			role: "region",
			"aria-label": "Node details"
		});
		const d = selected != null ? model.details(selected) : null;
		if (!d) details.appendChild(h("div", {
			class: "backstory-player-empty",
			text: "Select a node in the tree or use Inspect on the replay."
		}));
		else {
			const crumbs = h("div", { class: "backstory-player-crumbs" });
			d.breadcrumb.forEach((b, i) => {
				if (i) crumbs.appendChild(h("span", {
					class: "backstory-player-muted",
					text: "›"
				}));
				crumbs.appendChild(h("button", {
					text: b.label,
					onclick: () => {
						if (b.nodeId != null) {
							selected = b.nodeId;
							for (const id of model.pathTo(b.nodeId)) expanded.add(id);
							draw();
						}
					}
				}));
			});
			details.appendChild(crumbs);
			const row = h("div", { style: "display:flex;gap:6px;flex-wrap:wrap;margin-top:6px" });
			row.appendChild(h("button", {
				class: "backstory-player-btn",
				text: "Copy selector",
				onclick: () => ctx.actions.copy?.(d.selector)
			}));
			row.appendChild(h("button", {
				class: "backstory-player-btn",
				text: "Copy XPath",
				onclick: () => ctx.actions.copy?.(d.xpath)
			}));
			if (d.testId) row.appendChild(h("button", {
				class: "backstory-player-btn",
				text: "Copy data-testid",
				onclick: () => ctx.actions.copy?.(d.testId)
			}));
			details.appendChild(row);
			details.appendChild(h("div", {
				class: "backstory-player-mono backstory-player-muted",
				style: "margin-top:4px;word-break:break-all",
				text: d.selector
			}));
			details.appendChild(h("h4", { text: `Attributes (${d.attributes.length})` }));
			const dl = h("dl", { class: "backstory-player-kv" });
			for (const [k, v] of d.attributes) {
				dl.appendChild(h("dt", { text: k }));
				dl.appendChild(h("dd", { text: v }));
			}
			details.appendChild(dl);
			if (d.box) {
				details.appendChild(h("h4", { text: "Box model" }));
				const b = d.box;
				details.appendChild(h("div", { class: "backstory-player-box" }, h("div", { class: "m" }, h("span", {
					class: "l",
					text: `margin ${b.margin.join(" ")}`
				}), h("div", { class: "b" }, h("span", {
					class: "l",
					text: `border ${b.border.join(" ")}`
				}), h("div", { class: "p" }, h("span", {
					class: "l",
					text: `padding ${b.padding.join(" ")}`
				}), h("div", {
					class: "c",
					text: `${Math.round(b.content.w)} × ${Math.round(b.content.h)}`
				}))))));
			}
			details.appendChild(h("h4", { text: "Computed styles" }));
			const cs = h("dl", { class: "backstory-player-kv" });
			for (const [k, v] of d.computed) {
				cs.appendChild(h("dt", { text: k }));
				cs.appendChild(h("dd", { text: v }));
			}
			details.appendChild(cs);
		}
		split.appendChild(details);
		root.appendChild(split);
		tree.querySelector("[aria-selected=\"true\"]")?.scrollIntoView?.({ block: "nearest" });
	};
	const setInspect = (on) => {
		const p = ctx.player;
		if (!p) return;
		p.setInspect(on);
		offInspect?.();
		offInspect = null;
		if (on) offInspect = p.on("inspect", (e) => {
			if (e.picked && e.nodeId != null) {
				selected = e.nodeId;
				for (const id of model.pathTo(e.nodeId)) expanded.add(id);
				inspect = false;
				p.setInspect(false);
				draw();
			}
		});
	};
	return {
		update(next) {
			ctx = next;
			if (next.mirror && (!model || model.mirror !== next.mirror)) model = new ElementsModel(next.mirror);
			draw();
		},
		destroy() {
			offInspect?.();
			root.remove();
		}
	};
}
/**
* Panels re-render into a fresh DOM tree on every playhead tick, which resets
* the scroll position of their scrollable regions. During live playback ticks
* arrive continuously, so without this the agent cannot scroll a panel at all.
* Resting at the bottom means "follow the tail" instead: rows that arrive stay
* in view. Scrolling up releases the follow and counts what arrived meanwhile,
* which is what the pill offers a way back to.
* Console and Elements are excluded: they follow the playhead by design.
*/
var SCROLLABLE = ".backstory-player-body,.backstory-player-drawer,.backstory-player-split>*";
/** Sub-pixel rounding leaves `scrollTop` a hair short of the true bottom. */
var BOTTOM_SLACK = 8;
function keepScroll(factory) {
	return (container) => {
		const panel = factory(container);
		/** The list; any further regions (an open drawer, say) only keep their place. */
		const main = () => container.querySelector(SCROLLABLE);
		/** Column headers and the empty placeholder are furniture, not arrivals to count. */
		const rowCount = (el) => el.childElementCount - el.querySelectorAll(":scope > .backstory-player-empty, :scope > .head").length;
		/**
		* Null until the first update tells us whether this is a live panel. Live
		* panels start following; a recording starts wherever the agent left the
		* playhead, and scrolling to the bottom is what opts into the tail.
		*/
		let released = null;
		let unseen = 0;
		const sync = () => {
			pill.hidden = unseen === 0;
			pill.textContent = `${unseen} new ↓`;
		};
		const toTail = (el) => {
			if (el) el.scrollTop = el.scrollHeight;
			unseen = 0;
			released = false;
			sync();
		};
		const pill = h("button", {
			type: "button",
			class: "backstory-player-follow",
			onclick: () => toTail(main())
		});
		pill.hidden = true;
		const onScroll = (e) => {
			if (e.target !== main()) return;
			const el = e.target;
			const away = el.scrollTop < el.scrollHeight - el.clientHeight - BOTTOM_SLACK;
			released = away;
			if (away) return;
			unseen = 0;
			sync();
		};
		container.style.position = container.style.position || "relative";
		container.appendChild(pill);
		container.addEventListener("scroll", onScroll, true);
		return {
			update(ctx) {
				if (released === null) released = ctx.input.live !== true;
				const before = [...container.querySelectorAll(SCROLLABLE)].map((el) => ({
					top: el.scrollTop,
					rows: rowCount(el)
				}));
				panel.update(ctx);
				const after = container.querySelectorAll(SCROLLABLE);
				if (!before.length || after.length !== before.length) return;
				after.forEach((el, i) => {
					const prev = before[i];
					if (i === 0 && !released) {
						el.scrollTop = el.scrollHeight;
						return;
					}
					el.scrollTop = prev.top;
					if (i === 0 && ctx.input.live) unseen += Math.max(0, rowCount(el) - prev.rows);
				});
				sync();
			},
			destroy() {
				container.removeEventListener("scroll", onScroll, true);
				pill.remove();
				panel.destroy();
			}
		};
	};
}
var PANEL_FACTORIES = {
	elements: createElementsPanel,
	console: createConsolePanel,
	network: keepScroll(createNetworkPanel),
	media: keepScroll(createMediaPanel),
	performance: keepScroll(createPerformancePanel),
	state: keepScroll(createStatePanel),
	application: keepScroll(createApplicationPanel),
	accessibility: keepScroll(createAccessibilityPanel),
	issues: keepScroll(createIssuesPanel),
	custom: keepScroll(createCustomPanel),
	assist: keepScroll(createAssistPanel)
};
var PANEL_IDS = Object.keys(PANEL_FACTORIES);
var PANEL_TITLES = {
	elements: "Elements",
	console: "Console",
	network: "Network",
	media: "Media",
	performance: "Performance",
	state: "State",
	application: "Application",
	accessibility: "Accessibility",
	issues: "Signals",
	custom: "Custom events",
	assist: "Assist"
};
function createMediaPanel(container) {
	let ctx = null;
	let serverCalls = [];
	let asked = false;
	let openPc = null;
	const root = h("div", { class: "backstory-player backstory-player-media" });
	container.appendChild(root);
	const laneSvg = (lane, span, now, freezes) => {
		const w = 100;
		const hgt = 28;
		const total = Math.max(1, span.to - span.from);
		const x = (t) => (t - span.from) / total * w;
		const y = (v) => 25 - Math.min(v, lane.max) / lane.max * 22;
		const pts = lane.points.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.t).toFixed(2)},${y(p.v).toFixed(2)}`).join("");
		const ns = "http://www.w3.org/2000/svg";
		const svg = document.createElementNS(ns, "svg");
		svg.setAttribute("viewBox", `0 0 ${w} ${hgt}`);
		svg.setAttribute("preserveAspectRatio", "none");
		svg.setAttribute("class", "backstory-player-mlane");
		svg.setAttribute("role", "img");
		const last = lane.current;
		svg.setAttribute("aria-label", `${lane.label}: ${lane.points.length} samples, ${last == null ? "no value" : `${round(last)} ${lane.unit}`.trim()} at the playhead, peak ${round(lane.max)} ${lane.unit}`.trim());
		if (lane.id === "fps" || lane.id === "kbps") for (const f of freezes) {
			const r = document.createElementNS(ns, "rect");
			r.setAttribute("x", String(x(f.start)));
			r.setAttribute("y", "0");
			r.setAttribute("width", String(Math.max(.4, x(f.end) - x(f.start))));
			r.setAttribute("height", String(hgt));
			r.setAttribute("class", "freeze");
			svg.appendChild(r);
		}
		if (pts) {
			const p = document.createElementNS(ns, "path");
			p.setAttribute("d", pts);
			p.setAttribute("class", "line");
			p.setAttribute("style", `stroke:var(${lane.colorVar})`);
			svg.appendChild(p);
			const lp = lane.points.filter((q) => q.t <= now).pop();
			if (lp) {
				const c = document.createElementNS(ns, "circle");
				c.setAttribute("cx", String(x(lp.t)));
				c.setAttribute("cy", String(y(lp.v)));
				c.setAttribute("r", "1.6");
				c.setAttribute("class", "dot");
				c.setAttribute("style", `fill:var(${lane.colorVar})`);
				svg.appendChild(c);
			}
		}
		const ph = document.createElementNS(ns, "line");
		ph.setAttribute("x1", String(x(now)));
		ph.setAttribute("x2", String(x(now)));
		ph.setAttribute("y1", "0");
		ph.setAttribute("y2", String(hgt));
		ph.setAttribute("class", "ph");
		svg.appendChild(ph);
		const wrap = h("div", { class: "backstory-player-mlane-wrap" });
		wrap.appendChild(svg);
		return wrap;
	};
	const round = (v) => v >= 100 ? String(Math.round(v)) : v >= 10 ? v.toFixed(0) : v.toFixed(v < 1 ? 2 : 1);
	const draw = () => {
		if (!ctx) return;
		clear(root);
		if (!asked && ctx.fetchers?.calls) {
			asked = true;
			ctx.fetchers.calls().then((cs) => {
				serverCalls = cs;
				draw();
			}).catch(() => void 0);
		}
		const m = deriveMedia(ctx.input, serverCalls);
		const body = h("div", { class: "backstory-player-body" });
		const counts = mediaCounts(m);
		const totalFreezeMs = m.calls.reduce((sum, call) => sum + Math.max(call.summary?.freezeMsTotal ?? 0, call.freezes.reduce((n, freeze) => n + freeze.ms, 0)), 0);
		const scores = m.calls.map((call) => call.callQualityScore ?? call.qualityScore).filter((score) => typeof score === "number");
		const worstScore = scores.length ? Math.min(...scores) : void 0;
		const worstBucket = qualityBucket(worstScore);
		if (m.empty) {
			body.appendChild(h("div", { class: "backstory-player-empty" }, h("div", { text: ctx.input.live ? "No WebRTC activity in this session yet." : "No WebRTC activity in this segment." }), h("div", {
				class: "backstory-player-muted",
				style: "margin-top:6px",
				text: "Enable webrtc in the SDK config to record call quality. Audio is never captured. Pictures of the remote video are stored only when the project turns them on and the visitor agrees."
			})));
			root.appendChild(body);
			return;
		}
		body.appendChild(h("div", { class: "backstory-player-summary" }, h("span", { text: `${counts.calls} call${counts.calls === 1 ? "" : "s"}` }), worstScore != null ? h("span", {
			class: worstBucket.id === "bad" || worstBucket.id === "poor" ? "bad" : "",
			text: `${worstScore} quality · ${worstBucket.label}`
		}) : null, counts.failed ? h("span", {
			class: "bad",
			text: `${counts.failed} failed to connect`
		}) : null, counts.freezes ? h("span", {
			class: "bad",
			text: `${counts.freezes} freeze${counts.freezes === 1 ? "" : "s"} · ${formatCallDuration(totalFreezeMs)}`
		}) : null, counts.mediaErrors ? h("span", {
			class: "bad",
			text: `${counts.mediaErrors} capture error${counts.mediaErrors === 1 ? "" : "s"}`
		}) : null, h("span", {
			class: "backstory-player-muted",
			text: "audio is not recorded"
		})));
		if (m.sessionEvents.length) {
			body.appendChild(h("div", { class: "backstory-player-h" }, h("span", { text: "Devices and capture" })));
			for (const ev of m.sessionEvents) body.appendChild(h("div", {
				class: `backstory-player-row ${ev.future ? "future" : ""}`,
				role: "button",
				tabindex: 0,
				onclick: () => ctx.actions.seek(ev.t)
			}, timeCell(ctx, ev.t), h("div", {}, h("span", {
				class: `backstory-player-sev ${ev.severity === "error" ? "high" : ev.severity === "warn" ? "medium" : "info"}`,
				text: ev.severity === "error" ? "error" : ev.severity === "warn" ? "warn" : "info"
			}), h("span", { text: ev.label }))));
		}
		for (const call of m.calls) {
			const q = call.callQualityScore ?? call.qualityScore;
			const bucket = qualityBucket(q);
			const callFreezeCount = Math.max(call.freezes.length, call.summary?.freezeCount ?? 0);
			const isOpen = openPc === null || openPc === call.pc;
			const head = h("div", { class: `backstory-player-mcall-head ${call.future ? "future" : ""}` }, h("button", {
				class: "backstory-player-btn",
				"aria-expanded": isOpen,
				text: isOpen ? "▾" : "▸",
				"aria-label": `Toggle ${call.pc}`,
				onclick: () => {
					openPc = isOpen ? "__none__" : call.pc;
					draw();
				}
			}), h("strong", { text: call.pc }), h("span", {
				class: `backstory-player-cstate ${call.state}`,
				text: call.state
			}), call.failedToConnect ? h("span", {
				class: "backstory-player-sev high",
				text: "never connected"
			}) : null, h("span", {
				class: "backstory-player-pill",
				title: call.relayed ? "Peer-to-peer was blocked; media went through a TURN relay" : "Direct peer-to-peer media path"
			}, h("span", {
				"aria-hidden": "true",
				text: call.relayed ? "⇄" : "→"
			}), h("span", { text: call.relayed ? " relayed" : " direct" })), call.localCandidate ? h("span", {
				class: "backstory-player-muted",
				text: `${call.localCandidate} → ${call.remoteCandidate ?? "?"}`
			}) : null, call.networkType ? h("span", {
				class: "backstory-player-muted",
				text: call.networkType
			}) : null, callFreezeCount ? h("span", {
				class: "backstory-player-sev high",
				text: `${callFreezeCount} freeze${callFreezeCount === 1 ? "" : "s"}`
			}) : null, h("span", {
				class: "backstory-player-qs",
				title: "Composite of latency, loss, jitter, freezes, and audio concealment"
			}, h("span", {
				class: "track",
				role: "img",
				"aria-label": `Quality score ${q ?? 0} of 100, ${bucket.label}`
			}, h("i", { style: `width:${Math.max(2, q ?? 0)}%;background:var(${bucket.colorVar})` })), h("span", { text: q == null ? "no data" : `${q} ${bucket.label}` })));
			body.appendChild(head);
			if (!isOpen) continue;
			const meta = h("dl", { class: "backstory-player-kv" });
			const kv = (k, v) => {
				if (v == null) return;
				meta.appendChild(h("dt", { text: k }));
				meta.appendChild(h("dd", { text: v }));
			};
			kv("Setup", call.setupMs != null ? formatCallDuration(call.setupMs) : call.failedToConnect ? "never connected" : "in progress");
			kv("Duration", formatCallDuration(call.durationMs));
			kv("ICE", call.iceState ?? void 0);
			kv("ICE restarts", call.iceRestarts ? String(call.iceRestarts) : void 0);
			kv("Path changes", call.candidatePairChanges ? String(call.candidatePairChanges) : void 0);
			kv("TURN configured", call.hasTurn == null ? void 0 : call.hasTurn ? "yes" : "no");
			if (call.frozenNow) kv("Frozen now", `${formatCallDuration(call.frozenNow.ms)} (video not arriving)`);
			body.appendChild(meta);
			const span = {
				from: ctx.input.startTs,
				to: ctx.input.endTs
			};
			for (const lane of call.lanes) {
				const cur = lane.current;
				body.appendChild(h("div", { class: "backstory-player-h" }, h("span", { text: lane.label }), h("span", {
					class: "backstory-player-muted",
					text: cur == null ? "–" : `${round(cur)} ${lane.unit}`.trim()
				})));
				body.appendChild(laneSvg(lane, span, ctx.input.now, call.freezes));
			}
			if (callFreezeCount) {
				const callFreezeMs = Math.max(call.summary?.freezeMsTotal ?? 0, call.freezes.reduce((a, f) => a + f.ms, 0));
				body.appendChild(h("div", { class: "backstory-player-legend" }, h("span", {}, h("i", { class: "sw-freeze" }), "video frozen"), h("span", {
					class: "backstory-player-muted",
					text: `${callFreezeCount} freeze${callFreezeCount === 1 ? "" : "s"}, ${formatCallDuration(callFreezeMs)} total`
				})));
			}
			if (call.tracks.length) {
				body.appendChild(h("div", { class: "backstory-player-h" }, h("span", { text: "Tracks at the playhead" })));
				const tbl = h("div", { class: "backstory-player-mtracks" });
				tbl.appendChild(h("div", { class: "backstory-player-mtrack head" }, h("span", { text: "Track" }), h("span", { text: "Codec" }), h("span", { text: "Res" }), h("span", { text: "Loss" }), h("span", { text: "Jitter" }), h("span", { text: "Rate" })));
				for (const tr of call.tracks) {
					const c = tr.current;
					tbl.appendChild(h("div", { class: `backstory-player-mtrack ${tr.muted || !tr.enabled ? "muted" : ""} ${tr.ended ? "ended" : ""}` }, h("span", {}, h("span", { text: `${tr.direction} ${tr.media}` }), tr.label ? h("span", {
						class: "backstory-player-muted",
						title: tr.label,
						text: ` · ${tr.label}`
					}) : null, tr.muted ? h("span", {
						class: "backstory-player-sev high",
						style: "margin-left:6px",
						text: "not arriving"
					}) : null, !tr.enabled ? h("span", {
						class: "backstory-player-sev medium",
						style: "margin-left:6px",
						text: "app muted"
					}) : null, tr.ended ? h("span", {
						class: "backstory-player-muted",
						text: " ended"
					}) : null), h("span", {
						class: "backstory-player-mono",
						text: (tr.codec ?? "–").replace(/^(audio|video)\//, "")
					}), h("span", { text: tr.media === "video" && c?.fps != null ? `${tr.resolution ?? "–"} · ${round(c.fps)} fps` : tr.resolution ?? "–" }), h("span", {
						class: (c?.lossPct ?? 0) >= 5 ? "bad" : "",
						text: c?.lossPct == null ? "–" : `${round(c.lossPct)}%`
					}), h("span", { text: c?.jitterMs == null ? "–" : `${round(c.jitterMs)} ms` }), h("span", { text: c?.kbps == null ? "–" : `${round(c.kbps)} kbps` })));
					if (c?.qualityLimitation && c.qualityLimitation !== "none") tbl.appendChild(h("div", { class: "backstory-player-mnote" }, h("span", { text: `Encoder limited by ${c.qualityLimitation}${c.limitedByCpuPct ? ` (${Math.round(c.limitedByCpuPct)}% of the window)` : ""}` })));
					if (c?.concealmentPct != null && c.concealmentPct >= 5) tbl.appendChild(h("div", { class: "backstory-player-mnote bad" }, h("span", { text: `Audio concealment ${round(c.concealmentPct)}%: the decoder is filling gaps, which is audible` })));
					if (c?.frameDropPct != null && c.frameDropPct >= 1) tbl.appendChild(h("div", { class: "backstory-player-mnote bad" }, h("span", { text: `${round(c.frameDropPct)}% of video frames dropped in this sample window` })));
					if ((c?.nackCount ?? 0) > 0 || (c?.pliCount ?? 0) > 0) tbl.appendChild(h("div", { class: "backstory-player-mnote" }, h("span", { text: `Recovery traffic: ${c?.nackCount ?? 0} retransmission request${c?.nackCount === 1 ? "" : "s"}, ${c?.pliCount ?? 0} keyframe request${c?.pliCount === 1 ? "" : "s"}` })));
					if (c?.stalled) tbl.appendChild(h("div", { class: "backstory-player-mnote bad" }, h("span", { text: "No packets moved in this window while the track was live" })));
				}
				body.appendChild(tbl);
			}
			body.appendChild(h("div", { class: "backstory-player-h" }, h("span", { text: "Events" }), h("span", {
				class: "backstory-player-muted",
				text: "click to seek"
			})));
			for (const ev of call.markers) body.appendChild(h("div", {
				class: `backstory-player-row ${ev.future ? "future" : ""}`,
				role: "button",
				tabindex: 0,
				"aria-current": ctx.input.now >= ev.t && ctx.input.now - ev.t < 1e3 ? "true" : void 0,
				onclick: () => {
					ctx.actions.seek(ev.t);
					if ((ev.kind === "track_muted" || ev.kind === "resolution_change") && ctx.mirror) {
						const vid = findVideoNode(ctx.mirror);
						if (vid != null) ctx.actions.highlightNode?.(vid, {
							ms: 2500,
							label: ev.kind === "track_muted" ? "video stopped arriving" : ev.label
						});
					}
				}
			}, timeCell(ctx, ev.t), h("div", {}, h("span", {
				class: `backstory-player-sev ${ev.severity === "error" ? "high" : ev.severity === "warn" ? "medium" : "info"}`,
				text: ev.severity === "error" ? "error" : ev.severity === "warn" ? "warn" : "info"
			}), h("span", { text: ev.label }))));
		}
		root.appendChild(body);
		const frozen = m.calls.find((c) => c.frozenNow);
		if (frozen?.frozenNow && ctx.mirror) {
			const vid = findVideoNode(ctx.mirror);
			if (vid != null) ctx.actions.highlightNode?.(vid, {
				ms: 1200,
				label: `video frozen ${formatCallDuration(frozen.frozenNow.ms)}`,
				color: "#d03b3b"
			});
		}
	};
	return {
		update(next) {
			ctx = next;
			draw();
		},
		destroy() {
			root.remove();
		}
	};
}
/** First <video> placeholder in the mirror, used to label freezes on the replay. */
function findVideoNode(mirror) {
	for (const [id, node] of mirror.entries()) if (node.nodeType === 1 && (node.tagName ?? "").toLowerCase() === "video") return id;
	return null;
}
//#endregion
//#region src/panels/styles.ts
/** Styles for the vanilla panel renderers. Hosts inject this once (scoped by the `backstory-player-` prefix). */
var PANEL_STYLES = `
.backstory-player{font:12px/1.45 ui-sans-serif,system-ui,sans-serif;color:var(--fg,#e5e7eb);height:100%;display:flex;flex-direction:column;min-height:0}
.backstory-player *{box-sizing:border-box}
.backstory-player-toolbar{display:flex;gap:6px;align-items:center;padding:6px 8px;border-bottom:1px solid var(--border,#1f2937);flex-wrap:wrap}
.backstory-player-toolbar input[type=search],.backstory-player-toolbar input[type=text]{flex:1;min-width:120px;height:26px;border:1px solid var(--border-strong,#374151);border-radius:6px;background:var(--bg,#0b0f19);color:inherit;padding:0 8px;font:inherit}
.backstory-player-chip{height:22px;padding:0 8px;font-size:11px;border-radius:999px;border:1px solid var(--border-strong,#374151);background:transparent;color:var(--fg-muted,#9ca3af);cursor:pointer;font:inherit;font-size:11px}
.backstory-player-chip[aria-pressed=true]{background:var(--accent-soft,rgba(99,102,241,.16));border-color:var(--accent,#6366f1);color:var(--fg,#e5e7eb)}
.backstory-player-chip.danger[aria-pressed=true]{border-color:var(--danger,#ef4444)}
.backstory-player-btn{height:24px;padding:0 8px;border-radius:6px;border:1px solid var(--border-strong,#374151);background:var(--bg-elev-2,#1f2937);color:inherit;cursor:pointer;font:inherit;font-size:11px}
.backstory-player-btn:hover{border-color:var(--accent,#6366f1)}
.backstory-player-btn[aria-pressed=true]{background:var(--accent-soft,rgba(99,102,241,.16));border-color:var(--accent,#6366f1)}
/* The shade says there are rows past the edge, and only where there are: the
   first two layers are the surface's own fill and travel with the content
   (background-attachment: local), so they cover the shade at whichever end has
   been reached. A row clipped in half is otherwise the only cue, and macOS hides
   its overlay scrollbar until after the gesture this is meant to prompt. */
.backstory-player-body{flex:1;overflow:auto;min-height:0;
background:linear-gradient(var(--bg-elev,#111827) 30%,transparent) center top,linear-gradient(transparent,var(--bg-elev,#111827) 70%) center bottom,radial-gradient(farthest-side at 50% 0,color-mix(in srgb,var(--fg,#e5e7eb) 30%,transparent),transparent) center top,radial-gradient(farthest-side at 50% 100%,color-mix(in srgb,var(--fg,#e5e7eb) 30%,transparent),transparent) center bottom;
background-repeat:no-repeat;background-size:100% 20px,100% 20px,100% 11px,100% 11px;background-attachment:local,local,scroll,scroll}
.backstory-player-empty{padding:16px;color:var(--fg-subtle,#6b7280);text-align:center}
.backstory-player-follow{position:absolute;left:50%;bottom:10px;transform:translateX(-50%);z-index:2;height:24px;padding:0 10px;border-radius:999px;border:1px solid var(--accent,#6366f1);background:var(--accent,#6366f1);color:#fff;cursor:pointer;font:inherit;font-size:11px;box-shadow:0 2px 8px rgba(0,0,0,.35)}
.backstory-player-follow[hidden]{display:none}
.backstory-player-muted{color:var(--fg-muted,#9ca3af)}
.backstory-player-mono{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
.backstory-player-row{display:grid;grid-template-columns:56px 1fr;gap:8px;padding:5px 8px;border-bottom:1px solid var(--border,#1f2937);cursor:pointer;align-items:start}
.backstory-player-row:hover{background:var(--bg-elev-2,#1f2937)}
.backstory-player-row.future{opacity:.5}
.backstory-player-row[aria-current=true]{background:var(--accent-soft,rgba(99,102,241,.16));box-shadow:inset 3px 0 0 var(--accent,#6366f1)}
.backstory-player-row .t{font-variant-numeric:tabular-nums;color:var(--fg-subtle,#6b7280)}
.backstory-player-level{display:inline-block;min-width:14px;height:14px;line-height:14px;text-align:center;border-radius:3px;font-size:10px;margin-right:6px;vertical-align:middle;font-weight:700}
.backstory-player-level.error,.backstory-player-level.exception{background:var(--danger,#ef4444);color:#fff}
.backstory-player-level.warn{background:var(--warn,#f59e0b);color:#111}
.backstory-player-level.info,.backstory-player-level.log,.backstory-player-level.debug,.backstory-player-level.trace{background:var(--bg-elev-2,#1f2937);color:var(--fg-muted,#9ca3af)}
.backstory-player-pre{margin:4px 0 0;white-space:pre-wrap;word-break:break-word;font-family:ui-monospace,Menlo,monospace;font-size:11px;color:var(--fg-muted,#9ca3af);max-height:240px;overflow:auto;background:var(--bg,#0b0f19);padding:6px;border-radius:6px}
details.backstory-player-obj summary{cursor:pointer;color:var(--info,#38bdf8)}
.backstory-player-frames{margin:4px 0 0;padding:0;list-style:none;font-family:ui-monospace,Menlo,monospace;font-size:11px}
.backstory-player-frames li{padding:1px 0;color:var(--fg-muted,#9ca3af)}
.backstory-player-frames li.inapp{color:var(--fg,#e5e7eb)}
.backstory-player-frames li .orig{color:var(--ok,#10b981)}
.backstory-player-frames li a{color:var(--info,#38bdf8);cursor:pointer;text-decoration:underline dotted}
.backstory-player-ctx{margin:4px 0 0 12px;padding:4px 6px;border-left:2px solid var(--accent,#6366f1);font-family:ui-monospace,Menlo,monospace;font-size:11px;white-space:pre}
.backstory-player-ctx .cur{background:var(--accent-soft,rgba(99,102,241,.16));display:block}
.backstory-player-tabs{display:flex;gap:2px;padding:4px 8px 0;border-bottom:1px solid var(--border,#1f2937)}
.backstory-player-tab{padding:4px 10px;border:1px solid transparent;border-bottom:0;border-radius:6px 6px 0 0;background:transparent;color:var(--fg-muted,#9ca3af);cursor:pointer;font:inherit;font-size:11px}
.backstory-player-tab[aria-selected=true]{background:var(--bg-elev,#111827);color:var(--fg,#e5e7eb);border-color:var(--border,#1f2937)}
/* network */
.backstory-player-net{display:grid;grid-template-columns:52px 1fr 44px 56px 62px minmax(90px,1.2fr);gap:6px;padding:4px 8px;border-bottom:1px solid var(--border,#1f2937);align-items:center;cursor:pointer;font-size:11px}
.backstory-player-net.head{position:sticky;top:0;background:var(--bg-elev,#111827);color:var(--fg-subtle,#6b7280);text-transform:uppercase;letter-spacing:.04em;font-size:10px;cursor:default}
.backstory-player-net:hover{background:var(--bg-elev-2,#1f2937)}
.backstory-player-net.future{opacity:.5}
.backstory-player-net[aria-current=true]{box-shadow:inset 3px 0 0 var(--accent,#6366f1)}
.backstory-player-net .path{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.backstory-player-net .path small{color:var(--fg-subtle,#6b7280)}
.backstory-player-net.ws{grid-template-columns:minmax(120px,1.3fr) 88px 72px 72px 78px minmax(72px,1fr)}
.backstory-player-status.ok{color:var(--ok,#10b981)}
.backstory-player-status.warn{color:var(--warn,#f59e0b)}
.backstory-player-status.bad{color:var(--danger,#ef4444)}
.backstory-player-status.live{color:var(--ok,#10b981)}
.backstory-player-ws-spark{position:relative;height:16px;background:var(--bg,#0b0f19);border-radius:3px;overflow:hidden}
.backstory-player-ws-spark i{position:absolute;width:2px;border-radius:1px;min-height:5px}
.backstory-player-ws-spark i.send{top:1px;height:6px;background:var(--info,#38bdf8)}
.backstory-player-ws-spark i.receive{top:9px;height:6px;background:#a855f7}
.backstory-player-wf{position:relative;height:10px;background:var(--bg,#0b0f19);border-radius:3px;overflow:hidden}
.backstory-player-wf i{position:absolute;top:0;bottom:0;border-radius:3px;background:var(--info,#38bdf8);min-width:2px}
.backstory-player-wf i.failed{background:var(--danger,#ef4444)}
.backstory-player-wf i.resource{background:var(--fg-subtle,#6b7280)}
.backstory-player-wf i.ws{background:#a855f7}
.backstory-player-drawer{border-top:1px solid var(--border,#1f2937);padding:8px;max-height:45%;overflow:auto;background:var(--bg-elev,#111827)}
.backstory-player-drawer h4{margin:8px 0 4px;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:var(--fg-subtle,#6b7280)}
.backstory-player-ws-frame{padding:6px 0;border-top:1px solid var(--border,#1f2937)}
.backstory-player-ws-frame:first-of-type{border-top:0}
.backstory-player-kv{display:grid;grid-template-columns:minmax(80px,auto) 1fr;gap:2px 10px;font-size:11px}
.backstory-player-kv dt{color:var(--fg-muted,#9ca3af);font-family:ui-monospace,Menlo,monospace}
.backstory-player-kv dd{margin:0;word-break:break-all}
.backstory-player-timing{display:flex;height:12px;border-radius:3px;overflow:hidden;background:var(--bg,#0b0f19)}
.backstory-player-timing i{display:block;height:100%}
.backstory-player-timing i.dns{background:#22d3ee}.backstory-player-timing i.connect{background:#a78bfa}.backstory-player-timing i.tls{background:#f472b6}.backstory-player-timing i.waiting{background:#fbbf24}.backstory-player-timing i.download{background:#34d399}.backstory-player-timing i.total{background:var(--info,#38bdf8)}
.backstory-player-legend{display:flex;gap:10px;flex-wrap:wrap;font-size:10px;color:var(--fg-muted,#9ca3af);margin-top:4px}
.backstory-player-legend i{display:inline-block;width:8px;height:8px;border-radius:2px;margin-right:4px;vertical-align:middle}
.backstory-player-span{display:grid;grid-template-columns:1fr 120px 60px;gap:6px;font-size:11px;padding:2px 0;align-items:center}
.backstory-player-span .bar{position:relative;height:8px;background:var(--bg,#0b0f19);border-radius:2px}
.backstory-player-span .bar i{position:absolute;top:0;bottom:0;background:var(--accent,#6366f1);border-radius:2px;min-width:2px}
.backstory-player-span.error .bar i{background:var(--danger,#ef4444)}
/* performance */
.backstory-player-cards{display:grid;grid-template-columns:repeat(auto-fill,minmax(96px,1fr));gap:8px;padding:8px}
.backstory-player-card{border:1px solid var(--border,#1f2937);border-radius:8px;padding:8px;background:var(--bg-elev,#111827);cursor:pointer;text-align:left;color:inherit;font:inherit}
.backstory-player-card .name{font-size:10px;color:var(--fg-subtle,#6b7280);text-transform:uppercase;letter-spacing:.04em}
.backstory-player-card .val{font-size:18px;font-weight:600;margin-top:2px}
.backstory-player-card.good .val{color:var(--ok,#10b981)}.backstory-player-card.needs-improvement .val{color:var(--warn,#f59e0b)}.backstory-player-card.poor .val{color:var(--danger,#ef4444)}
.backstory-player-card.future{opacity:.5}
.backstory-player-lane{position:relative;height:18px;background:var(--bg,#0b0f19);border-radius:4px;margin:4px 8px;overflow:hidden}
.backstory-player-lane i{position:absolute;top:2px;bottom:2px;min-width:2px;border-radius:2px;background:var(--warn,#f59e0b)}
.backstory-player-lane i.self{background:var(--danger,#ef4444)}
.backstory-player-lane i.act{top:0;bottom:0;border-radius:0;background:var(--accent,#6366f1)}
.backstory-player-lane .ph{position:absolute;top:0;bottom:0;width:2px;background:var(--fg,#e5e7eb)}
.backstory-player-h{margin:10px 8px 2px;font-size:10px;text-transform:uppercase;letter-spacing:.04em;color:var(--fg-subtle,#6b7280);display:flex;justify-content:space-between}
.backstory-player-memory{margin:4px 8px 12px;padding:8px;border:1px solid var(--border,#1f2937);border-radius:8px;background:var(--bg-elev,#111827)}
.backstory-player-memory-stats{display:flex;gap:18px;flex-wrap:wrap;margin-bottom:6px}
.backstory-player-memory-stats>span{display:flex;align-items:flex-start;gap:6px;color:var(--fg-muted,#9ca3af);font-size:10px}
.backstory-player-memory-stats i{width:8px;height:8px;margin-top:3px;border-radius:50%;background:var(--info,#38bdf8)}
.backstory-player-memory-stats .recorder i{background:#a855f7}
.backstory-player-memory-stats .cpu i{background:#f59e0b}
.backstory-player-memory-stats strong{display:block;color:var(--fg,#e5e7eb);font-size:12px;font-weight:500}
.backstory-player-memory-chart{display:block;width:100%;height:74px;border-radius:4px;background:linear-gradient(to bottom,rgba(239,68,68,.06),transparent 35%),var(--bg,#0b0f19);overflow:visible}
.backstory-player-memory-chart polyline{fill:none;stroke-width:1.5;vector-effect:non-scaling-stroke}
.backstory-player-memory-chart polyline.heap,.backstory-player-memory-chart circle.heap{stroke:var(--info,#38bdf8);fill:var(--info,#38bdf8)}
.backstory-player-memory-chart polyline.recorder,.backstory-player-memory-chart circle.recorder{stroke:#a855f7;fill:#a855f7}
.backstory-player-memory-chart circle{cursor:pointer;vector-effect:non-scaling-stroke}
.backstory-player-memory-chart line.pressure{stroke:var(--warn,#f59e0b);stroke-width:1;stroke-dasharray:2 2;vector-effect:non-scaling-stroke}
.backstory-player-memory-chart line.pressure.critical{stroke:var(--danger,#ef4444)}
.backstory-player-memory-chart line.playhead{stroke:var(--fg,#e5e7eb);stroke-width:1;opacity:.65;vector-effect:non-scaling-stroke}
.backstory-player-memory p{margin:6px 0 0;font-size:10px}
/* elements */
.backstory-player-tree{padding:4px 0;font-family:ui-monospace,Menlo,monospace;font-size:11px}
.backstory-player-node{display:flex;align-items:center;gap:4px;padding:1px 6px;cursor:pointer;white-space:nowrap}
.backstory-player-node:hover{background:var(--bg-elev-2,#1f2937)}
.backstory-player-node[aria-selected=true]{background:var(--accent-soft,rgba(99,102,241,.16))}
.backstory-player-node .tw{width:12px;color:var(--fg-subtle,#6b7280);display:inline-block;text-align:center;flex:none}
.backstory-player-node .tag{color:#93c5fd}.backstory-player-node .attr{color:#fcd34d}.backstory-player-node .txt{color:var(--fg-muted,#9ca3af)}.backstory-player-node .masked{color:#f472b6}
.backstory-player-crumbs{display:flex;gap:4px;flex-wrap:wrap;padding:6px 8px;border-bottom:1px solid var(--border,#1f2937);font-family:ui-monospace,Menlo,monospace;font-size:11px}
.backstory-player-crumbs button{background:none;border:0;color:var(--info,#38bdf8);cursor:pointer;font:inherit;padding:0}
.backstory-player-box{margin:8px auto;width:200px;font-size:10px;text-align:center}
.backstory-player-box .m{background:rgba(249,115,22,.25);padding:14px 4px 4px;position:relative}.backstory-player-box .b{background:rgba(250,204,21,.25);padding:14px 4px 4px;position:relative}.backstory-player-box .p{background:rgba(34,197,94,.25);padding:14px 4px 4px;position:relative}.backstory-player-box .c{background:rgba(59,130,246,.35);padding:8px}
.backstory-player-box span.l{position:absolute;left:4px;top:2px;color:var(--fg-muted,#9ca3af)}
/* Proportional rows, not pixel minimums: the dock is often shorter than
   120px+160px, and fixed minimums push the drawer past the panel so its last
   rows are clipped with no reachable scrollbar. Each pane scrolls instead. */
.backstory-player-split{display:grid;grid-template-rows:minmax(0,1fr) minmax(0,45%);height:100%;min-height:0}
.backstory-player-split>*{min-height:0;overflow:auto}
/* The grid track already sizes the drawer; its standalone cap would resolve
   against that track and shrink it to 45% of 45%. */
.backstory-player-split>.backstory-player-drawer{max-height:none}
/* state */
.backstory-player-diff{list-style:none;margin:0;padding:0;font-family:ui-monospace,Menlo,monospace;font-size:11px}
.backstory-player-diff li{padding:1px 0;word-break:break-all}
.backstory-player-diff .add{color:var(--ok,#10b981)}.backstory-player-diff .remove{color:var(--danger,#ef4444)}.backstory-player-diff .replace{color:var(--warn,#f59e0b)}
.backstory-player-rtree{font-family:ui-monospace,Menlo,monospace;font-size:11px;padding:4px 8px}
.backstory-player-rtree details{margin-left:10px}.backstory-player-rtree summary{cursor:pointer}
.backstory-player-rtree .props{color:var(--fg-subtle,#6b7280)}
/* a11y & issues */
.backstory-player-sev{display:inline-block;padding:0 6px;border-radius:999px;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.03em;margin-right:6px}
.backstory-player-sev.critical,.backstory-player-sev.high{background:var(--danger,#ef4444);color:#fff}.backstory-player-sev.serious,.backstory-player-sev.medium{background:var(--warn,#f59e0b);color:#111}.backstory-player-sev.moderate,.backstory-player-sev.low{background:var(--info,#38bdf8);color:#111}.backstory-player-sev.minor,.backstory-player-sev.info{background:var(--bg-elev-2,#1f2937);color:var(--fg-muted,#9ca3af)}
.backstory-player-issue{padding:8px;border-bottom:1px solid var(--border,#1f2937)}
.backstory-player-ai-output{margin:8px;border:1px solid #a855f7;border-radius:8px;background:var(--bg-elev,#111827)}
.backstory-player-ai-mark{margin-bottom:6px;color:#c084fc;font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase}
.backstory-player-issue.active{background:var(--accent-soft,rgba(99,102,241,.16))}
.backstory-player-issue.future{opacity:.55}
.backstory-player-issue .title{display:flex;align-items:center;gap:4px;flex-wrap:wrap}
.backstory-player-issue.signal-row{display:grid;grid-template-columns:52px minmax(0,1fr);gap:10px;align-items:start}
.backstory-player-issue .signal-time{font-size:11px;font-variant-numeric:tabular-nums;color:var(--fg-subtle,#6b7280);padding-top:2px}
.backstory-player-issue .signal-copy{min-width:0}
.backstory-player-issue .signal-copy .backstory-player-sev{margin:0 0 0 4px}
.backstory-player-issue .ev{display:inline-block;margin:4px 4px 0 0;padding:0 6px;border-radius:999px;border:1px solid var(--border-strong,#374151);font-size:10px;cursor:pointer;background:transparent;color:inherit}
.backstory-player-ai{margin:8px;padding:10px;border-radius:8px;border:1px solid var(--border,#1f2937);background:linear-gradient(135deg,rgba(99,102,241,.12),rgba(168,85,247,.12))}
.backstory-player-summary{display:flex;gap:10px;padding:6px 8px;font-size:11px;color:var(--fg-muted,#9ca3af);border-bottom:1px solid var(--border,#1f2937);flex-wrap:wrap}
/* media (WebRTC) */
.backstory-player-media .backstory-player-summary .bad{color:var(--danger,#ef4444)}
.backstory-player-mcall-head.future{opacity:.5}
.backstory-player-mcall-head{display:flex;align-items:center;gap:6px;flex-wrap:wrap;padding:8px;border-top:1px solid var(--border,#1f2937);background:var(--bg-elev,#111827);position:sticky;top:0;z-index:1}
.backstory-player-cstate{padding:0 6px;border-radius:999px;font-size:10px;border:1px solid var(--border-strong,#374151);color:var(--fg-muted,#9ca3af)}
.backstory-player-cstate.connected{border-color:var(--ok,#10b981);color:var(--ok,#10b981)}
.backstory-player-cstate.failed{border-color:var(--danger,#ef4444);color:var(--danger,#ef4444)}
.backstory-player-cstate.disconnected{border-color:var(--warn,#f59e0b);color:var(--warn,#f59e0b)}
.backstory-player-pill{display:inline-flex;align-items:center;gap:2px;padding:0 6px;border-radius:999px;font-size:10px;background:var(--bg-elev-2,#1f2937);color:var(--fg-muted,#9ca3af)}
.backstory-player-qs{display:inline-flex;align-items:center;gap:6px;margin-left:auto;font-size:11px;font-variant-numeric:tabular-nums}
.backstory-player-qs .track{display:block;width:64px;height:6px;border-radius:3px;background:var(--bg,#0b0f19);overflow:hidden}
.backstory-player-qs i{display:block;height:6px;border-radius:3px;min-width:2px}
.backstory-player-mlane-wrap{margin:0 8px;height:28px;background:var(--bg,#0b0f19);border-radius:4px;overflow:hidden}
svg.backstory-player-mlane{display:block;width:100%;height:28px}
svg.backstory-player-mlane .line{fill:none;stroke-width:1.5;vector-effect:non-scaling-stroke;stroke-linejoin:round}
svg.backstory-player-mlane .dot{r:1.6}
svg.backstory-player-mlane .ph{stroke:var(--fg,#e5e7eb);stroke-width:1;vector-effect:non-scaling-stroke}
svg.backstory-player-mlane .freeze{fill:rgba(208,59,59,.28)}
.backstory-player-legend i.sw-freeze{background:rgba(208,59,59,.55)}
.backstory-player-mtracks{margin:4px 8px;font-size:11px}
.backstory-player-mtrack{display:grid;grid-template-columns:minmax(110px,1.4fr) minmax(56px,.8fr) 64px 52px 64px 74px;gap:6px;padding:4px 0;border-bottom:1px solid var(--border,#1f2937);align-items:center}
.backstory-player-mtrack.head{color:var(--fg-subtle,#6b7280);text-transform:uppercase;letter-spacing:.04em;font-size:10px}
.backstory-player-mtrack.muted{background:rgba(208,59,59,.08)}
.backstory-player-mtrack.ended{opacity:.55}
.backstory-player-mtrack .bad{color:var(--danger,#ef4444)}
.backstory-player-mnote{padding:2px 0 4px;color:var(--fg-muted,#9ca3af);font-size:10px}
.backstory-player-mnote.bad{color:var(--danger,#ef4444)}
`;
//#endregion
//#region src/assets.ts
var AssetResolver = class {
	map = /* @__PURE__ */ new Map();
	opts;
	hashed = /* @__PURE__ */ new Map();
	constructor(opts) {
		this.opts = {
			...opts,
			assetBase: opts.assetBase.replace(/\/$/, "")
		};
	}
	get size() {
		return this.map.size;
	}
	/** Load a session's url → hash map. Tolerant of the three response shapes. */
	load(res) {
		if (Array.isArray(res)) {
			for (const e of res) this.map.set(normalizeUrl(e.url), e.urlHash);
			return;
		}
		if ("items" in res && Array.isArray(res.items)) {
			for (const e of res.items) this.map.set(normalizeUrl(e.url), e.urlHash);
			return;
		}
		for (const [url, hash] of Object.entries(res)) if (typeof hash === "string") this.map.set(normalizeUrl(url), hash);
	}
	/** Register a hash for a URL (used with hashUnknown after `prepare`). */
	set(url, hash) {
		this.map.set(normalizeUrl(url), hash);
	}
	/** Where the archived copy lives for a URL, or null when unknown. */
	archivedUrl(url) {
		const key = normalizeUrl(url);
		const hash = this.map.get(key) ?? (this.opts.hashUnknown ? this.hashed.get(key) : void 0);
		if (!hash) return null;
		return `${this.opts.assetBase}/${hash}${this.opts.query ? `?${this.opts.query}` : ""}`;
	}
	/** Rewrite for the Mirror: archived copy or the original URL. */
	rewrite(url) {
		if (!url || /^(data:|blob:|about:|javascript:|#)/i.test(url)) return url;
		return this.archivedUrl(url) ?? url;
	}
	/** The Mirror-facing rewriter. */
	get rewriter() {
		return (url) => this.rewrite(url);
	}
	/**
	* Pre-hash URLs client-side (WebCrypto) so `hashUnknown` can try the archive
	* for URLs the map did not list. Resolves when all hashes are ready.
	*/
	async prepare(urls) {
		const pending = [];
		for (const u of urls) {
			const key = normalizeUrl(u);
			if (this.map.has(key) || this.hashed.has(key)) continue;
			pending.push(sha256Hex(key).then((h) => void this.hashed.set(key, h)));
		}
		await Promise.all(pending);
	}
};
/** Strip the fragment; keep query (the archive keys on the full URL). */
function normalizeUrl(url) {
	const i = url.indexOf("#");
	return i === -1 ? url : url.slice(0, i);
}
async function sha256Hex(text) {
	const subtle = globalThis.crypto?.subtle;
	if (!subtle) return fnv1a64Hex(text);
	const buf = await subtle.digest("SHA-256", new TextEncoder().encode(text));
	return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}
function fnv1a64Hex(s) {
	let h1 = 2166136261;
	let h2 = 16777619;
	for (let i = 0; i < s.length; i++) {
		const c = s.charCodeAt(i);
		h1 = Math.imul(h1 ^ c, 16777619) >>> 0;
		h2 = Math.imul(h2 ^ c, 2166136261) >>> 0;
	}
	return (h1.toString(16).padStart(8, "0") + h2.toString(16).padStart(8, "0")).padEnd(64, "0");
}
/** Collect asset URLs referenced by a snapshot document (for `prepare`). */
function collectAssetUrls(root) {
	const out = /* @__PURE__ */ new Set();
	const visit = (n) => {
		if (n["type"] === "element") {
			const attrs = n["attrs"] ?? {};
			const tag = String(n["tag"] ?? "").toLowerCase();
			for (const a of [
				"src",
				"href",
				"poster"
			]) {
				const v = attrs[a];
				if (v && !(tag === "a" && a === "href") && !/^(data:|blob:|#)/i.test(v)) out.add(v);
			}
			if (attrs["srcset"]) for (const p of attrs["srcset"].split(",")) out.add(p.trim().split(/\s+/)[0] ?? "");
			const cssTexts = [];
			const css = n["cssText"];
			if (typeof css === "string") cssTexts.push(css);
			if (tag === "style") {
				for (const c of n["childNodes"] ?? []) if (c["type"] === "text" && typeof c["text"] === "string") cssTexts.push(c["text"]);
			}
			for (const text of cssTexts) for (const m of text.matchAll(/url\(\s*['"]?([^'")]+)['"]?\s*\)/g)) if (m[1] && !/^(data:|blob:|#)/i.test(m[1])) out.add(m[1]);
			for (const c of n["childNodes"] ?? []) visit(c);
			for (const c of n["shadow"] ?? []) visit(c);
		} else if (n["type"] === "document") for (const c of n["childNodes"] ?? []) visit(c);
	};
	visit(root);
	out.delete("");
	return [...out];
}
//#endregion
//#region src/live.ts
/**
* Appends live chunks to a Player. The first chunk loads the session; later
* chunks are merged into the timeline in seq order (duplicates ignored).
*/
var LiveFeed = class {
	player;
	refs = [];
	chunks = /* @__PURE__ */ new Map();
	started = false;
	_following = true;
	_latestSeq = 0;
	listeners = /* @__PURE__ */ new Map();
	lagMs;
	constructor(player, opts = {}) {
		this.player = player;
		this.lagMs = Math.max(0, opts.lagMs ?? 0);
	}
	on(type, fn) {
		let set = this.listeners.get(type);
		if (!set) {
			set = /* @__PURE__ */ new Set();
			this.listeners.set(type, set);
		}
		set.add(fn);
		return () => set.delete(fn);
	}
	emit(type, payload) {
		const set = this.listeners.get(type);
		if (!set) return;
		for (const fn of set) fn(payload);
	}
	/** True while the playhead tracks the newest chunk. */
	get following() {
		return this._following;
	}
	get latestSeq() {
		return this._latestSeq;
	}
	/** Relative ms of the live edge (the end of the newest chunk). */
	get liveEdgeMs() {
		return this.player.duration;
	}
	/** Seconds the agent is behind the live edge (0 when following). */
	get behindMs() {
		return Math.max(0, this.player.duration - this.player.currentTime);
	}
	/** Append a chunk from the live stream. Out-of-order and duplicate seqs are handled. */
	append(chunk) {
		const ref = {
			seq: chunk.seq,
			firstTs: chunk.firstTs,
			lastTs: chunk.lastTs,
			size: 0,
			checkpointId: chunk.checkpointId
		};
		if (this.refs.some((r) => r.seq === chunk.seq)) return;
		this.chunks.set(chunk.seq, chunk);
		this.refs = [...this.refs, ref].sort((a, b) => a.seq - b.seq);
		this._latestSeq = Math.max(this._latestSeq, chunk.seq);
		if (!this.started) {
			this.started = true;
			this.player.loadChunks([chunk]);
		} else {
			const hadRenderableFrame = this.player.renderable;
			this.player.timeline.setManifest(this.refs);
			this.player.timeline.addChunk(chunk);
			if (!hadRenderableFrame && this.player.renderable) this.player.loadChunks([...this.chunks.values()].sort((a, b) => a.seq - b.seq));
		}
		if (this._following) this.toLiveEdge();
		this.emit("chunk", {
			seq: chunk.seq,
			following: this._following,
			duration: this.player.duration
		});
	}
	/** Move the playhead to the live edge (minus the configured lag). */
	toLiveEdge() {
		if (!this.started) return;
		this.player.pause();
		this.player.seek(Math.max(0, this.player.duration - this.lagMs));
	}
	/** Stop following so the agent can scrub back in time. */
	pause() {
		if (!this._following) return;
		this._following = false;
		this.player.pause();
		this.emit("follow", { following: false });
	}
	/** Resume following and jump to the live edge. */
	backToLive() {
		const was = this._following;
		this._following = true;
		this.toLiveEdge();
		if (!was) this.emit("follow", { following: true });
	}
	/** Seek to a relative time; implies pausing the follow. */
	async scrub(relMs) {
		this.pause();
		await this.player.seek(relMs);
	}
	destroy() {
		this.listeners.clear();
	}
};
/**
* Canvas overlay that draws AnnotationShape values with viewport-normalized
* coordinates (0..1) over its container. Shapes expire after their TTL.
*/
var AnnotationLayer = class {
	container;
	el;
	items = /* @__PURE__ */ new Map();
	resolveBox;
	defaultTtl;
	laserTtl;
	now;
	resizeObs = null;
	destroyed = false;
	constructor(container, opts = {}) {
		this.container = container;
		this.resolveBox = opts.resolveBox ?? (() => null);
		this.defaultTtl = opts.defaultTtlMs ?? 1e4;
		this.laserTtl = opts.laserTtlMs ?? 1500;
		this.now = opts.now ?? (() => Date.now());
		this.el = document.createElement("canvas");
		this.el.className = "backstory-annotation-layer";
		this.el.setAttribute("aria-hidden", "true");
		Object.assign(this.el.style, {
			position: "absolute",
			inset: "0",
			width: "100%",
			height: "100%",
			pointerEvents: "none",
			zIndex: "40"
		});
		container.appendChild(this.el);
		if (typeof ResizeObserver !== "undefined") {
			this.resizeObs = new ResizeObserver(() => this.render());
			this.resizeObs.observe(container);
		}
	}
	/** Non-expired shapes, oldest first. */
	shapes() {
		this.prune();
		return [...this.items.values()].map((i) => i.shape);
	}
	add(shape) {
		if (this.destroyed) return;
		const ttl = shape.kind === "laser" ? this.laserTtl : shape.ttlMs > 0 ? shape.ttlMs : this.defaultTtl;
		const prev = this.items.get(shape.id);
		if (prev?.timer) clearTimeout(prev.timer);
		const item = {
			shape,
			expiresAt: this.now() + ttl,
			timer: null
		};
		item.timer = setTimeout(() => {
			this.items.delete(shape.id);
			this.render();
		}, ttl);
		this.items.set(shape.id, item);
		this.render();
	}
	remove(id) {
		const it = this.items.get(id);
		if (!it) return;
		if (it.timer) clearTimeout(it.timer);
		this.items.delete(id);
		this.render();
	}
	clear() {
		for (const it of this.items.values()) if (it.timer) clearTimeout(it.timer);
		this.items.clear();
		this.render();
	}
	prune() {
		const t = this.now();
		for (const [id, it] of this.items) if (it.expiresAt <= t) {
			if (it.timer) clearTimeout(it.timer);
			this.items.delete(id);
		}
	}
	/** Redraw all shapes. Safe in environments without a 2D context (no-op). */
	render() {
		if (this.destroyed) return;
		this.prune();
		const w = this.container.clientWidth || this.el.width || 1;
		const h = this.container.clientHeight || this.el.height || 1;
		if (this.el.width !== w) this.el.width = w;
		if (this.el.height !== h) this.el.height = h;
		let ctx = null;
		try {
			ctx = this.el.getContext("2d");
		} catch {
			ctx = null;
		}
		if (!ctx) return;
		ctx.clearRect(0, 0, w, h);
		for (const { shape } of this.items.values()) drawShape(ctx, shape, w, h, this.resolveBox);
	}
	destroy() {
		this.clear();
		this.destroyed = true;
		this.resizeObs?.disconnect();
		this.resizeObs = null;
		this.el.remove();
	}
};
function drawShape(ctx, s, w, h, resolveBox) {
	ctx.save();
	ctx.lineCap = "round";
	ctx.lineJoin = "round";
	switch (s.kind) {
		case "pen":
			ctx.strokeStyle = s.color;
			ctx.lineWidth = s.width;
			ctx.beginPath();
			s.points.forEach((p, i) => i === 0 ? ctx.moveTo(p.x * w, p.y * h) : ctx.lineTo(p.x * w, p.y * h));
			ctx.stroke();
			break;
		case "arrow": {
			const x1 = s.from.x * w;
			const y1 = s.from.y * h;
			const x2 = s.to.x * w;
			const y2 = s.to.y * h;
			ctx.strokeStyle = s.color;
			ctx.fillStyle = s.color;
			ctx.lineWidth = 3;
			ctx.beginPath();
			ctx.moveTo(x1, y1);
			ctx.lineTo(x2, y2);
			ctx.stroke();
			const a = Math.atan2(y2 - y1, x2 - x1);
			ctx.beginPath();
			ctx.moveTo(x2, y2);
			ctx.lineTo(x2 - 12 * Math.cos(a - Math.PI / 6), y2 - 12 * Math.sin(a - Math.PI / 6));
			ctx.lineTo(x2 - 12 * Math.cos(a + Math.PI / 6), y2 - 12 * Math.sin(a + Math.PI / 6));
			ctx.closePath();
			ctx.fill();
			break;
		}
		case "rect":
			ctx.strokeStyle = s.color;
			ctx.lineWidth = 3;
			ctx.strokeRect(s.x * w, s.y * h, s.w * w, s.h * h);
			break;
		case "highlight": {
			const b = resolveBox(s.nodeId);
			if (!b) break;
			ctx.strokeStyle = s.color;
			ctx.lineWidth = 3;
			ctx.setLineDash([6, 4]);
			ctx.strokeRect(b.x - 3, b.y - 3, b.w + 6, b.h + 6);
			break;
		}
		case "laser":
			ctx.fillStyle = "rgba(239, 68, 68, 0.9)";
			ctx.beginPath();
			ctx.arc(s.x * w, s.y * h, 6, 0, Math.PI * 2);
			ctx.fill();
			break;
		case "text":
			ctx.fillStyle = s.color;
			ctx.font = "14px system-ui, sans-serif";
			ctx.fillText(s.text, s.x * w, s.y * h);
	}
	ctx.restore();
}
//#endregion
export { AnnotationLayer, AssetResolver, COMMON_STYLE_KEYS, CanvasTracks, CommandReplayer, CursorOverlay, DEFAULT_HALF_MS, ElementsModel, HighlightOverlay, KEY_STEP_MS, LiveFeed, MARK, Mirror, PANEL_FACTORIES, PANEL_IDS, PANEL_STYLES, PANEL_TITLES, Player, SEVERITY_ORDER, SLOW_MS, SelectionModel, Symbolicator, Timeline, VITAL_THRESHOLDS, VideoTrack, WebCodecsFrameDecoder, accessibleName, ancestorsOf, applyPatch, attributesOf, auditAccessibility, boxModelOf, clear, collectAssetUrls, computedStylesOf, consoleCounts, contrastRatio, createAccessibilityPanel, createApplicationPanel, createAssistPanel, createConsolePanel, createCustomPanel, createElementsPanel, createIssuesPanel, createMediaPanel, createNetworkPanel, createPerformancePanel, createStatePanel, cssEscape, defaultImageDecoder, deriveApplication, deriveConsole, deriveIssues, deriveMedia, deriveNetwork, derivePerformance, deriveState, describeElement, describeGap, describeMarker, fmtBytes, fmtClock, fmtMs, focusPath, formatCallDuration, formatRange, formatTime, formatVital, h, humanKind, isHashedToken, isReactTree, issueCounts, mediaCounts, mediaErrorLabel, normalizeUrl, parseColor, parseStack, parseUA, qualityBucket, rateVital, reasonLabel, reconstructState, rel, rewriteCssUrls, rewriteSrcset, sampleAt, selectorFor, serverLevel, serverRows, sha256Hex, spanTree, splitUrl, summarizeDiff, summarizeNetwork, timingSegments, toCurl, traceForRow, trackStatsAt, tryJson, worstQuality, wsCadenceMs, wsCloseLabel, wsSparkTicks, wsStateLabel, xpathFor };

//# sourceMappingURL=index.js.map